<template>
  <v-sheet class="register-view-container">
    <div
      class="register-data-layout"
      :class="{ 'register-data-layout--with-details': isRowPanelOpen }">
      <!-- Table Section -->
      <v-data-table
      class="register-table elevation-0 d-flex flex-column table-sticky custom-table flex-grow-1"
      :class="{ 'register-table--zebra': orderedHeaders.length >= ZEBRA_MIN_COLUMNS }"
      :style="pinStyle"
      ref="table"
      :options.sync="options"
      :headers="visibleHeaders"
      :items="tableRows"
      :items-per-page="options.itemsPerPage"
      :server-items-length="totalItems"
      :loading="loading"
      :item-class="getRowClass"
      @dblclick:row="openRowDetails"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
        itemsPerPageText: 'Row/Page',
        itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
      }">
      <!-- Enhanced Table Toolbar (matching RegisterManagement style) -->
      <template v-slot:top>
        <div class="table-toolbar-enhanced">
          <!-- Left Section: Title, Count, Column Controls -->
          <div class="toolbar-left">
            <div class="toolbar-title-section">
              <columns-visibility-control
                v-if="currentRegisterType"
                v-model="availableColumns"
                :defaults="defaultColumns"
                :withColumnsOrder="true"
                :storageKey="`registers-register-${currentRegisterType.toLowerCase()}`"
                :schemaUpdatedAt="schemaUpdatedAt" />
              <span class="toolbar-title ml-2">Data</span>
              <v-chip
                v-if="totalItems > 0"
                small
                color="primary"
                outlined
                class="ml-2 count-chip">
                {{ totalItems.toLocaleString() }} {{ totalItems === 1 ? 'row' : 'rows' }}
              </v-chip>
            </div>

            <!-- Central Separated: who owns the rows (facilitySummary) -->
            <template v-if="facilityChips">
              <v-chip small outlined class="register-ownership-chip">
                <v-icon left small>mdi-map-marker-multiple-outline</v-icon>
                {{ facilityChips.facilitiesLabel }}
              </v-chip>
              <button
                v-if="facilityChips.unassignedCount > 0 || unassignedFilterActive"
                type="button"
                class="register-unassigned-chip"
                :aria-pressed="unassignedFilterActive ? 'true' : 'false'"
                title="Rows with no Facility are not visible to any member Facility"
                @click="toggleUnassignedFilter">
                <v-icon small>mdi-alert-circle-outline</v-icon>
                {{ facilityChips.unassignedLabel }}
              </button>
            </template>
            <span v-if="scopeHint" class="register-scope-hint" :title="scopeHint.title">
              <v-icon small>{{ scopeHint.icon }}</v-icon>
              {{ scopeHint.text }}
            </span>

            <!-- Selection indicator -->
            <v-chip
              v-if="hasSelectedRows"
              small
              color="primary"
              close
              class="ml-3 pl-4 selection-chip"
              @click:close="clearSelection">
              <v-icon left small>mdi-checkbox-marked-outline</v-icon>
              {{ selectedRowCount }} selected
            </v-chip>
          </div>

          <!-- Right Section: Search & Export -->
          <div class="toolbar-right">
            <!-- Facility filter (central Separated): All, each Facility with its count, Unassigned -->
            <v-menu v-if="facilityFilterMenuItems.length" offset-y bottom left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="register-facility-filter"
                  :aria-label="`Filter rows by Facility: ${facilityFilterButtonLabel}`"
                  v-bind="attrs"
                  v-on="on">
                  <v-icon left small>mdi-map-marker-outline</v-icon>
                  <span class="register-facility-filter__label">{{ facilityFilterButtonLabel }}</span>
                  <v-icon right small>mdi-menu-down</v-icon>
                </v-btn>
              </template>
              <v-list dense class="register-facility-filter-menu" aria-label="Filter rows by Facility">
                <v-list-item
                  v-for="option in facilityFilterMenuItems"
                  :key="option.key"
                  :input-value="option.selected"
                  :aria-current="option.selected ? 'true' : null"
                  @click="setFacilityFilter(option.filter)">
                  <v-list-item-icon class="register-facility-filter-menu__check">
                    <v-icon v-if="option.selected" small color="primary">mdi-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :class="{ 'register-facility-filter-menu__warning': option.warning }">
                      {{ option.label }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <span class="register-facility-filter-menu__count">{{ formatCount(option.count) }}</span>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Search Field -->
            <v-text-field
              v-model="searchFilter"
              placeholder="Search all columns..."
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              hide-details
              clearable
              class="search-field"
              @input="onSearchFilterChange" />

            <!-- Edit Selected Button -->
            <v-btn
              v-if="hasSelectedRows && canEditRows"
              small
              color="primary"
              outlined
              class="export-btn"
              @click="editSelectedRows">
              <v-icon left small>mdi-playlist-edit</v-icon>
              Edit Selected ({{ selectedRowCount }})
            </v-btn>

            <!-- Export Selected Button -->
            <v-btn
              v-if="hasSelectedRows && canExport"
              small
              color="primary"
              outlined
              class="export-btn"
              @click="exportSelected">
              <v-icon left small>mdi-checkbox-multiple-marked</v-icon>
              Export Selected ({{ selectedRowCount }})
            </v-btn>

            <!-- Isolate Selected in 3D -->
            <v-btn
              v-if="hasSelectedRows"
              small
              color="primary"
              outlined
              class="ml-2"
              @click="isolateSelectedIn3d">
              <v-icon left small>mdi-cube-scan</v-icon>
              Isolate in 3D ({{ selectedRowCount }})
            </v-btn>

            <!-- Export All/Filtered Button -->
            <v-btn
              v-if="canExport"
              small
              color="primary"
              class="export-btn"
              @click="exportFiltered">
              <v-icon left small>mdi-tray-arrow-down</v-icon>
              Export
              <v-chip
                v-if="hasActiveFilters"
                x-small
                color="white"
                text-color="primary"
                class="ml-2">
                Filtered
              </v-chip>
            </v-btn>
          </div>
        </div>
        <div
          v-if="hasRelationshipAttentionFilter"
          class="relationship-attention-banner"
          :class="{ 'relationship-attention-banner--error': attentionFilterProblem }"
          :role="attentionFilterProblem ? 'alert' : 'status'"
          aria-live="polite">
          <v-icon :color="attentionFilterProblem ? 'error' : 'warning'">
            {{ attentionFilterProblem ? 'mdi-alert-circle-outline' : 'mdi-table-filter' }}
          </v-icon>
          <div class="relationship-attention-banner__content">
            <strong>{{ attentionFilterProblem ? attentionFilterProblemTitle : 'Relationship attention filter' }}</strong>
            <span v-if="attentionFilterProblem">{{ attentionFilterProblem.detail || 'The attention rows could not be loaded.' }}</span>
            <span v-else>{{ loading ? 'Loading rows that need attention...' : `${tableRows.length} loaded on this page / ${totalItems} matching rows` }}</span>
          </div>
          <v-chip
            v-if="!attentionFilterProblem"
            x-small
            outlined
            color="warning"
            class="relationship-attention-banner__count">
            {{ loading ? 'Loading' : `${totalItems} matching` }}
          </v-chip>
          <v-btn small text :color="attentionFilterProblem ? 'error' : 'primary'" @click="clearRelationshipAttentionFilter">
            <v-icon left small>mdi-filter-remove-outline</v-icon>Clear filter
          </v-btn>
        </div>

        <div v-if="dataLoadState !== TABLE_LOAD_STATE.OK && tableRows.length" class="register-data-notice" role="alert">
          <v-icon small color="error">mdi-alert-circle-outline</v-icon>
          <span>These rows are from the last successful load. The server did not respond, so nothing was refreshed.</span>
          <v-btn small outlined color="primary" :loading="loading" @click="loadTableData">Try again</v-btn>
        </div>

        <!-- Central view of a populated Facility Group register with no mode: rows stay manageable here -->
        <div v-if="centralModeUnsetNoticeVisible" class="register-data-notice" role="status">
          <v-icon small class="register-data-notice__icon--warning">mdi-cog-outline</v-icon>
          <span>
            <strong>Sharing mode not set.</strong>
            Member Facilities can't see these {{ formatCount(totalItems) }} rows until AIVA support sets this
            register to Shared or Separated. You can still manage the rows here.
          </span>
        </div>

        <div v-if="unassignedFilterActive && dataLoadState === TABLE_LOAD_STATE.OK" class="register-data-notice" role="status">
          <v-icon small class="register-data-notice__icon--warning">mdi-alert-circle-outline</v-icon>
          <span>
            <strong>{{ formatCount(totalItems) }} {{ totalItems === 1 ? 'row has' : 'rows have' }} no Facility.</strong>
            In a Separated register, no member Facility can see these rows until each one has a Facility.
          </span>
          <v-btn small text color="primary" @click="clearFacilityFilter">
            <v-icon left small>mdi-filter-remove-outline</v-icon>Show all
          </v-btn>
        </div>
      </template>

      <!-- Custom header template with filter buttons -->
      <template
        v-for="header in visibleHeaders"
        v-slot:[`header.${header.value}`]="{ header: columnHeader }">
        <div :key="header.value" class="header-with-filter d-flex align-center">
          <span class="header-text">{{ columnHeader.text }}</span>
          <v-tooltip v-if="isColumnSortIndexed(header.value)" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                x-small
                class="sort-index-indicator ml-1"
                color="amber darken-2"
                v-bind="attrs"
                v-on="on">
                mdi-lightning-bolt
              </v-icon>
            </template>
            <span>Fast sorting enabled</span>
          </v-tooltip>
          <v-menu
            v-if="!HEADERS_WITHOUT_FILTER.includes(header.value) && !isBooleanHeader(header.value)"
            :value="activeFilterMenu === header.value"
            @input="(val) => toggleFilterMenu(header.value, val)"
            offset-y
            :close-on-content-click="false"
            nudge-left="10">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="ml-1 filter-btn"
                :class="{ 'filter-active': hasColumnFilter(header.value) }"
                v-bind="attrs"
                v-on="on">
                <v-icon small :color="hasColumnFilter(header.value) ? 'primary' : 'grey'">
                  {{ hasColumnFilter(header.value) ?
                    (getColumnFilterType(header.value) === 'select' ? 'mdi-filter-check' : 'mdi-filter') :
                    'mdi-filter-outline' }}
                </v-icon>
                <v-badge v-if="hasColumnFilter(header.value)" dot overlap color="primary">
                </v-badge>
              </v-btn>
            </template>

            <!-- Filter menu content -->
            <v-card class="filter-menu" width="360">
              <v-card-title class="py-2 px-3">
                <span class="text-subtitle-2">Filter {{ columnHeader.text }}</span>
              </v-card-title>
              <v-card-text class="pb-2">
                <!-- Filter Type Selection -->
                <v-radio-group
                  v-model="tempFilterType"
                  row
                  dense
                  class="mt-0 mb-3"
                  hide-details
                  @change="onFilterTypeChange">
                  <v-radio label="Search" value="search" class="mr-4"></v-radio>
                  <v-radio
                    :label="`Select ${loadingFilterValues.has(header.value) ? '(Loading...)' : ''}`"
                    value="select"
                    :disabled="isBooleanHeader(header.value)">
                  </v-radio>
                </v-radio-group>

                <!-- Search Input -->
                <v-text-field
                  v-if="tempFilterType === 'search'"
                  v-model="tempFilterValue"
                  label="Search..."
                  outlined
                  dense
                  clearable
                  hide-details
                  :disabled="tempFilterType !== 'search'"
                  @keydown.enter="applyColumnFilter(header.value)"
                  @keydown.escape="closeFilterMenu"
                  @input="onTempFilterChange">
                  <template v-slot:append>
                    <v-icon>mdi-magnify</v-icon>
                  </template>
                </v-text-field>

                <!-- Autocomplete Dropdown -->
                <v-autocomplete
                  v-else-if="tempFilterType === 'select'"
                  v-model="tempSelectedValue"
                  :items="columnFilterValues[header.value] || []"
                  label="Type to search values..."
                  placeholder="Start typing to filter..."
                  outlined
                  dense
                  clearable
                  hide-details
                  :loading="loadingFilterValues.has(header.value)"
                  :disabled="tempFilterType !== 'select'"
                  :search-input.sync="autocompleteSearch"
                  hide-no-data
                  @keydown.enter="applyColumnFilter(header.value)"
                  @keydown.escape="closeFilterMenu"
                  @input="onAutocompleteSelect">
                  <template v-slot:no-data>
                    <div class="px-4 py-2 text-center text-caption grey--text">
                      {{ loadingFilterValues.has(header.value) ? 'Loading values...' :
                         autocompleteSearch ? 'No matching values found' : 'Start typing to search...' }}
                    </div>
                  </template>
                  <template v-slot:append-item v-if="!loadingFilterValues.has(header.value) && !columnFilterNotes[header.value] && (!columnFilterValues[header.value] || columnFilterValues[header.value].length === 0)">
                    <div class="px-4 py-2 text-center text-caption grey--text">
                      No unique values found for this column
                    </div>
                  </template>
                </v-autocomplete>
                <!-- Values could not be read (no access, register not configured, failure): fixed copy only. -->
                <div
                  v-if="tempFilterType === 'select' && columnFilterNotes[header.value]"
                  class="register-filter-note text-caption text--secondary mt-2"
                  role="status">
                  {{ columnFilterNotes[header.value] }}
                </div>
              </v-card-text>
              <v-card-actions class="px-3 py-3">
                <v-btn small text color="grey" @click="clearColumnFilter(header.value)">
                  Clear
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn small text @click="closeFilterMenu"> Cancel </v-btn>
                <v-btn small color="primary" @click="applyColumnFilter(header.value)">
                  Apply
                </v-btn>
              </v-card-actions>
              <!-- Sort Index Toggle (dynamic columns only, requires Update permission) -->
              <div
                v-if="isDynamicColumn(header.value) && $has(permissions.Registers.Update)"
                class="sort-index-toggle px-3 py-2">
                <v-divider class="mb-2"></v-divider>
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon small class="mr-2" color="amber darken-2">mdi-lightning-bolt</v-icon>
                    <span class="text-caption font-weight-medium">Fast sorting</span>
                  </div>
                  <v-switch
                    :input-value="isColumnSortIndexed(header.value)"
                    :loading="togglingSortIndex.has(header.value)"
                    :disabled="togglingSortIndex.has(header.value)"
                    dense
                    hide-details
                    class="mt-0 pt-0 sort-index-switch"
                    @change="toggleSortIndex(header.value)">
                  </v-switch>
                </div>
                <div class="text-caption grey--text mt-1" style="font-size: 11px !important;">
                  Creates a database index for faster sorting
                </div>
              </div>
            </v-card>
          </v-menu>
        </div>
      </template>

      <!-- Selection Header -->
      <template v-slot:[`header.data-table-select`]>
        <v-simple-checkbox
          :value="allPageRowsSelected"
          :indeterminate="somePageRowsSelected"
          color="primary"
          @input="toggleSelectAllOnPage"
          :ripple="false" />
      </template>

      <!-- Selection Column -->
      <template v-slot:[`item.data-table-select`]="{ item }">
        <v-simple-checkbox
          :value="isRowSelected(item)"
          color="primary"
          @input="toggleRowSelection(item)"
          :ripple="false" />
      </template>

      <!-- Row Index Column -->
      <template v-slot:[`item.RowIndex`]="{ item }">
        <span class="font-weight-medium">{{ item.RowIndex }}</span>
      </template>

      <!-- Linked Tags Column: tinted chips, all up to 5, then 4 + "Show all" -->
      <template v-slot:[`item.LinkedTags`]="{ item }">
        <linked-tag-chips
          v-if="Array.isArray(item.LinkedTags) && item.LinkedTags.length > 0"
          :key="`linked-tags-${item.Id}`"
          :chips="linkedTagChips(item.LinkedTags)"
          :row-label="item.RowIndex"
          :disabled="rowDetailsIsSaving"
          @open="openLinkedRecord" />
        <span v-else class="register-empty-value">—</span>
      </template>

      <!-- Facility Column (central Separated only): owner, or an amber Unassigned -->
      <template v-slot:[`item.${REGISTER_TABLE_COLUMN.FACILITY}`]="{ item }">
        <span
          v-if="!rowOwner(item).unassigned"
          class="register-facility-cell"
          :title="rowOwner(item).name">
          <v-icon small>mdi-map-marker-outline</v-icon>
          <span>{{ rowOwner(item).name }}</span>
        </span>
        <span
          v-else
          class="register-facility-cell register-facility-cell--unassigned"
          title="No Facility. No member Facility can see this row.">
          —<span class="register-facility-cell__dot" aria-hidden="true"></span><i>Unassigned</i>
        </span>
      </template>

      <!-- Actions Column (right-pinned): two primary icons on hover / focus, the rest in ⋮ -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="register-row-actions">
          <!-- Edit Mode Actions -->
          <template v-if="isInlineEditingRow(item.Id)">
            <!-- Save Button with change indicator -->
            <v-btn
              icon
              small
              @click.stop="saveRowEdit(item)"
              :loading="isSavingRow(item.Id)"
              :disabled="getModifiedFieldsCount() === 0"
              :title="
                getModifiedFieldsCount() > 0
                  ? `Save ${getModifiedFieldsCount()} changes`
                  : 'No changes to save'
              ">
              <v-badge
                v-if="getModifiedFieldsCount() > 0"
                :content="getModifiedFieldsCount()"
                color="orange darken-1"
                overlap
                small>
                <v-icon size="20" color="success">mdi-check</v-icon>
              </v-badge>
              <v-icon v-else size="20" color="grey lighten-1">mdi-check</v-icon>
            </v-btn>
            <!-- Cancel Button -->
            <v-btn icon small @click.stop="cancelRowEdit" :title="'Cancel Changes'">
              <v-icon size="20" color="error">mdi-close</v-icon>
            </v-btn>
          </template>

          <!-- Normal Mode Actions -->
          <template v-else>
            <span class="register-row-actions__primary">
              <v-btn
                icon
                small
                @click.stop="openRowDetails(null, { item })"
                :title="`Open record ${item.RowIndex} details`"
                :aria-label="`Open record ${item.RowIndex} details`">
                <v-icon size="20" color="primary">mdi-card-account-details-outline</v-icon>
              </v-btn>

              <!-- Edit Icon -->
              <v-btn
                v-if="canEditRows"
                icon
                small
                :disabled="isRowPanelOpen || isSavingRow(item.Id)"
                @click.stop="startRowEdit(item)"
                :title="isRowPanelOpen ? 'Finish editing the open record first' : `Edit Row ${item.RowIndex}`"
                :aria-label="`Edit row ${item.RowIndex}`">
                <v-icon size="20" color="success">mdi-pencil</v-icon>
              </v-btn>

              <!-- Row History Icon (primary only when Edit is not available) -->
              <v-btn
                v-else
                icon
                small
                @click.stop="openRowAuditHistory(item)"
                title="View Row History"
                :aria-label="`View row ${item.RowIndex} history`">
                <v-icon size="20" color="blue-grey">mdi-history</v-icon>
              </v-btn>
            </span>

            <!-- More actions: History (when Edit is primary), View in 3D, View P&ID -->
            <v-menu v-if="rowMenuActions(item).length" bottom left offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  title="More actions"
                  :aria-label="`More actions for row ${item.RowIndex}`"
                  :loading="pidLoadingRowId === item.Id"
                  v-bind="attrs"
                  v-on="on">
                  <v-icon size="20">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="register-row-menu" :aria-label="`More actions for row ${item.RowIndex}`">
                <v-list-item
                  v-for="action in rowMenuActions(item)"
                  :key="action.key"
                  @click="runRowMenuAction(action.key, item, $event)">
                  <v-list-item-icon class="register-row-menu__icon">
                    <v-icon size="18">{{ action.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ action.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- P&ID Document chooser (several documents): opens where "View P&ID" was chosen -->
            <v-menu
              v-if="item.HasPIdDocument"
              :value="showPIdMenu && pidMenuTargetRow?.Id === item.Id"
              @input="(val) => { if (!val) { showPIdMenu = false; pidMenuTargetRow = null; } }"
              :close-on-content-click="true"
              :z-index="999"
              absolute
              :position-x="pidMenuPosition.x"
              :position-y="pidMenuPosition.y"
              transition="scale-transition"
            >
              <!-- P&ID Selection Menu (for multiple docs) -->
              <v-card class="pid-menu" elevation="8">
                <v-list dense>
                  <v-subheader>
                    <v-icon small class="mr-2">mdi-file-cad-box</v-icon>
                    Select P&ID Document
                  </v-subheader>
                  <v-divider />
                  <v-list-item
                    v-for="doc in pidMenuDocuments"
                    :key="doc.id"
                    @click="selectPIdDocument(doc)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon small :color="isPIdOtherFacility(doc) ? 'warning' : 'primary'">
                        {{ isPIdOtherFacility(doc) ? 'mdi-open-in-new' : 'mdi-file-pdf-box' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ doc.documentNumber || doc.documentTitle || 'Untitled' }}</v-list-item-title>
                      <v-list-item-subtitle v-if="isPIdOtherFacility(doc)">
                        {{ getFacilityName(doc.assetId) }} · Opens in new tab
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </template>
        </div>
      </template>

      <!-- Custom slot for register-type specific columns -->
      <template v-for="column in customColumns" v-slot:[`item.${column.value}`]="{ item }">
        <div :key="`${item.Id}-${column.value}`">
          <!-- Edit Mode -->
          <div
            v-if="isInlineEditingRow(item.Id)"
            class="edit-field-container"
            :key="`edit-${item.Id}-${column.value}`">
            <!-- Boolean Field Editor -->
            <v-select
              style="max-width: 250px;"
              v-if="isColumnType(column, 4)"
              v-model="editingData[column.value]"
              :items="[
                { text: 'Yes', value: true },
                { text: 'No', value: false },
              ]"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value),
              }"
              @change="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-select>

            <!-- Date Field Editor -->
            <v-text-field
              v-else-if="isColumnType(column, 3)"
              v-model="editingData[column.value]"
              type="date"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value),
              }"
              @keydown.enter="saveRowEdit(item)"
              @keydown.escape="cancelRowEdit"
              @input="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-text-field>

            <!-- Number Field Editor -->
            <v-text-field
              v-else-if="isColumnType(column, 2)"
              v-model.number="editingData[column.value]"
              type="number"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value),
              }"
              @keydown.enter="saveRowEdit(item)"
              @keydown.escape="cancelRowEdit"
              @input="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-text-field>

            <!-- Text Field Editor (Default) -->
            <v-text-field
              v-else
              v-model="editingData[column.value]"
              dense
              outlined
              hide-details
              :class="{
                'field-modified': isFieldModified(item, column.value),
                'field-unchanged': !isFieldModified(item, column.value),
              }"
              @keydown.enter="saveRowEdit(item)"
              @keydown.escape="cancelRowEdit"
              @input="onFieldChange(column.value, $event)">
              <template v-slot:prepend-inner v-if="isFieldModified(item, column.value)">
                <v-icon small color="orange darken-1">mdi-circle-edit-outline</v-icon>
              </template>
            </v-text-field>

            <!-- Change indicator -->
            <div v-if="isFieldModified(item, column.value)" class="field-change-indicator">
              <small class="text-caption grey--text">
                Changed from:
                <span class="original-value">{{ getOriginalValue(item, column.value) }}</span>
              </small>
            </div>
          </div>

          <!-- Display Mode -->
          <div v-else class="display-field" :key="`display-${item.Id}-${column.value}`">
            <!-- Boolean Values -->
            <v-icon
              v-if="
                isColumnType(column, 4) &&
                item[column.value] !== null &&
                item[column.value] !== undefined &&
                item[column.value] !== ''
              "
              :color="getBooleanValue(item[column.value]) ? 'success' : 'error'">
              {{ getBooleanValue(item[column.value]) ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>

            <!-- Date Values -->
            <div v-else-if="isColumnType(column, 3) && item[column.value]" class="date-cell">
              <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
              {{ $formatDate(item[column.value]) }}
            </div>

            <!-- Number Values -->
            <span v-else-if="isColumnType(column, 2)" class="font-weight-medium">
              {{ formatNumber(item[column.value]) }}
            </span>

            <!-- Text Values (Default) -->
            <v-tooltip bottom v-else-if="item[column.value]">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ item[column.value] }}
                </span>
              </template>
              <span>{{ item[column.value] }}</span>
            </v-tooltip>

            <!-- Empty Value -->
            <span v-else class="text-caption grey--text">—</span>
          </div>
        </div>
      </template>

      <!-- Loading State -->
      <template v-slot:loading>
        <div class="loading-container">
          <v-progress-circular indeterminate color="primary" size="32" width="4" class="mb-4">
          </v-progress-circular>
          <p class="text-subtitle-1 font-weight-medium">Loading Register...</p>
        </div>
      </template>

      <!-- No Data State: failures, not-configured registers and genuinely empty results are
           different facts. Server text is never rendered here; "Copy details" carries it. -->
      <template v-slot:no-data>
        <template v-if="!loading">
          <register-table-state-notice
            v-if="gateState === GATE_STATE.CONFIGURATION_REQUIRED"
            icon="mdi-table-cog"
            eyebrow="Facility Group register · sharing mode not set"
            title="Configuration required"
            show-mode-definitions>
            <b>{{ registerName || 'This register' }}</b>
            <template v-if="facilityGroupName"> belongs to <b>{{ facilityGroupName }}</b> and</template>
            <template v-else> is a Facility Group register that</template>
            already has rows. It has not been set to Shared or Separated, so AIVA can't tell which rows
            belong to {{ currentFacilityName || 'this Facility' }}. The rows are safe and unchanged. An
            administrator or AIVA support needs to set the mode. After that, the rows appear here.
            <template v-slot:actions>
              <v-btn small text color="primary" @click="copyRegisterDetails">
                <v-icon left small>mdi-content-copy</v-icon>Copy register details
              </v-btn>
              <span v-if="copiedNotice === 'register'" class="register-copied" role="status">Copied</span>
            </template>
          </register-table-state-notice>

          <register-table-state-notice
            v-else-if="gateState === GATE_STATE.FORBIDDEN"
            icon="mdi-lock-outline"
            title="You don't have access to these rows"
            role="alert">
            You don't have access to the rows of <b>{{ registerName || 'this register' }}</b>
            <template v-if="currentFacilityName"> in {{ currentFacilityName }}</template>.
            Ask your administrator if you need them.
            <template v-slot:actions>
              <v-btn small text color="primary" @click="copyFailureDetails">
                <v-icon left small>mdi-content-copy</v-icon>Copy details
              </v-btn>
              <span v-if="copiedNotice === 'failure'" class="register-copied" role="status">Copied</span>
            </template>
          </register-table-state-notice>

          <register-table-state-notice
            v-else-if="gateState === GATE_STATE.FAILED"
            icon="mdi-alert-circle-outline"
            tone="error"
            role="alert"
            title="Register data could not be loaded">
            AIVA couldn't load the rows<template v-if="registerName"> for <b>{{ registerName }}</b></template>
            just now. Nothing was changed. Try again in a moment. If it keeps failing, copy the details and
            send them to your administrator.
            <template v-slot:actions>
              <v-btn small color="primary" depressed :loading="loading" @click="loadTableData">Try again</v-btn>
              <v-btn small text color="primary" @click="copyFailureDetails">
                <v-icon left small>mdi-content-copy</v-icon>Copy details
              </v-btn>
              <span v-if="copiedNotice === 'failure'" class="register-copied" role="status">Copied</span>
            </template>
          </register-table-state-notice>

          <register-table-state-notice
            v-else-if="gateState === GATE_STATE.WAITING_FOR_FIRST_IMPORT"
            icon="mdi-table-cog"
            eyebrow="Facility Group register · sharing mode not set"
            title="Waiting for the first import"
            show-mode-definitions>
            <template v-if="viewContext === 'central'">
              This Facility Group register has no rows yet. The first import sets it to Shared or Separated,
              depending on whether that import assigns a Facility to each row.
            </template>
            <template v-else>
              <b>{{ registerName || 'This register' }}</b>
              <template v-if="facilityGroupName"> belongs to <b>{{ facilityGroupName }}</b> and</template>
              has no rows yet. The first import will decide whether it is Shared or Separated. Rows appear
              here after that import.
            </template>
          </register-table-state-notice>

          <register-table-state-notice v-else icon="mdi-table-off" title="No rows to show">
            This register has no rows matching the current search and filters.
          </register-table-state-notice>
        </template>
      </template>
      </v-data-table>

      <transition name="row-details-panel">
        <aside
          v-if="isRowPanelOpen"
          ref="rowDetailsPanel"
          class="row-details-panel"
          role="region"
          aria-labelledby="register-row-details-title"
          tabindex="-1"
          @keydown.esc.stop="requestCloseRowDetails">
          <header class="row-details-panel__header">
            <div class="row-details-panel__mark" aria-hidden="true">
              <v-icon color="primary" size="21">
                {{ bulkEditActive ? 'mdi-playlist-edit' : 'mdi-table-row' }}
              </v-icon>
            </div>
            <div class="row-details-panel__heading">
              <span>{{ panelEyebrow }}</span>
              <h2 id="register-row-details-title">{{ panelTitle }}</h2>
              <p>{{ registerName || 'Record details' }}</p>
            </div>
            <v-btn
              icon
              small
              class="row-details-panel__close"
              :aria-label="bulkEditActive ? 'Close bulk edit' : 'Close row details'"
              :title="bulkEditActive ? 'Close bulk edit' : 'Close row details'"
              :disabled="panelIsSaving"
              @click="requestCloseRowDetails">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </header>

          <div class="row-details-panel__body">
            <section class="row-details-panel__section" aria-labelledby="register-row-fields-title">
              <div class="row-details-panel__section-heading">
                <div>
                  <span>{{ bulkEditActive ? 'Apply to selection' : (canEditRows ? 'Edit record' : 'Information') }}</span>
                  <h3 id="register-row-fields-title">
                    {{ canEditRows ? 'Fields and values' : 'Record fields' }}
                  </h3>
                </div>
                <strong>{{ canEditRows ? panelEditableFields.length : rowDetailsFields.length }}</strong>
              </div>

              <p v-if="bulkEditActive && bulkEditSaving" class="row-details-panel__bulk-note">
                Saving {{ bulkEditRowCount }} {{ bulkEditRowCount === 1 ? 'Row' : 'Rows' }}...
              </p>
              <p v-else-if="bulkEditActive && bulkEditExceedsCap" class="row-details-panel__bulk-note">
                <v-icon size="16" color="warning">mdi-alert-outline</v-icon>
                {{ bulkEditCapMessage }}
              </p>
              <p v-else-if="bulkEditActive" class="row-details-panel__bulk-note">
                Only the fields you change here are written to the selected rows. Anything you
                leave untouched keeps its current value on every row.
              </p>

              <div
                v-if="canEditRows && panelEditableFields.length"
                class="row-details-panel__editor">
                <div
                  v-for="field in panelEditableFields"
                  :key="field.key"
                  class="row-details-panel__editor-field">
                  <v-select
                    v-if="isColumnType(field.column, 4)"
                    v-model="editingData[field.key]"
                    :items="[
                      { text: 'Yes', value: true },
                      { text: 'No', value: false },
                    ]"
                    :label="field.label"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="panelIsSaving"
                    :class="{ 'field-modified': isPanelFieldModified(field.key) }"
                    @change="onFieldChange(field.key, $event)" />
                  <v-text-field
                    v-else-if="isColumnType(field.column, 3)"
                    v-model="editingData[field.key]"
                    type="date"
                    :label="field.label"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="panelIsSaving"
                    :class="{ 'field-modified': isPanelFieldModified(field.key) }"
                    @input="onFieldChange(field.key, $event)" />
                  <v-text-field
                    v-else-if="isColumnType(field.column, 2)"
                    v-model.number="editingData[field.key]"
                    type="number"
                    :label="field.label"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="panelIsSaving"
                    :class="{ 'field-modified': isPanelFieldModified(field.key) }"
                    @input="onFieldChange(field.key, $event)" />
                  <v-textarea
                    v-else-if="isMultilineTextColumn(field.column)"
                    v-model="editingData[field.key]"
                    :label="field.label"
                    rows="1"
                    auto-grow
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="panelIsSaving"
                    :class="{ 'field-modified': isPanelFieldModified(field.key) }"
                    @input="onFieldChange(field.key, $event)" />
                  <v-text-field
                    v-else
                    v-model="editingData[field.key]"
                    type="text"
                    :label="field.label"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="panelIsSaving"
                    :class="{ 'field-modified': isPanelFieldModified(field.key) }"
                    @input="onFieldChange(field.key, $event)" />
                  <span
                    v-if="!bulkEditActive && isPanelFieldModified(field.key)"
                    class="row-details-panel__changed">
                    Changed from {{ panelOriginalValue(field) }}
                  </span>
                </div>
              </div>

              <dl
                v-if="!bulkEditActive && (rowDetailsReadOnlyFields.length || rowDetailsOwner)"
                class="row-details-panel__fields row-details-panel__fields--context">
                <div v-if="rowDetailsOwner">
                  <dt>Facility</dt>
                  <dd v-if="!rowDetailsOwner.unassigned">{{ rowDetailsOwner.name }}</dd>
                  <dd v-else class="register-facility-cell register-facility-cell--unassigned">
                    —<span class="register-facility-cell__dot" aria-hidden="true"></span><i>Unassigned</i>
                  </dd>
                </div>
                <div v-for="field in rowDetailsReadOnlyFields" :key="field.key">
                  <dt>{{ field.label }}</dt>
                  <dd :class="{ 'row-details-panel__value--empty': field.empty }">{{ field.value }}</dd>
                </div>
              </dl>

              <div v-if="!bulkEditActive && !rowDetailsFields.length" class="row-details-panel__empty">
                <v-icon size="28" color="grey lighten-1">mdi-text-box-outline</v-icon>
                <strong>No record fields to display</strong>
                <span>This row does not contain any user-visible values.</span>
              </div>
            </section>

            <section
              v-if="!bulkEditActive"
              class="row-details-panel__section"
              aria-labelledby="register-row-links-title">
              <div class="row-details-panel__section-heading">
                <div>
                  <span>Connections</span>
                  <h3 id="register-row-links-title">Linked records</h3>
                </div>
                <strong>{{ rowDetailsLinks.length }}</strong>
              </div>

              <ul v-if="rowDetailsLinks.length" class="row-details-panel__links">
                <li v-for="link in rowDetailsLinks" :key="link.key">
                  <button
                    type="button"
                    :disabled="!link.clickable || rowDetailsIsSaving"
                    :aria-label="linkAriaLabel(link)"
                    :title="link.clickable ? (link.opensNewTab ? 'Opens in a new tab' : 'Opens the Tag panel') : null"
                    @click="openLinkedRecord(link)">
                    <span class="row-details-panel__link-mark" aria-hidden="true">
                      <v-icon color="primary" size="18">{{ link.icon }}</v-icon>
                    </span>
                    <span class="row-details-panel__link-copy">
                      <strong>{{ link.label }}</strong>
                      <span>{{ linkSubtitle(link) }}</span>
                    </span>
                    <v-icon
                      v-if="link.clickable && link.opensNewTab"
                      class="row-details-panel__link-new-tab"
                      size="17"
                      aria-hidden="true">
                      mdi-open-in-new
                    </v-icon>
                    <v-icon v-else-if="link.clickable" color="primary" size="18" aria-hidden="true">
                      mdi-chevron-right
                    </v-icon>
                  </button>
                </li>
              </ul>
              <div v-else class="row-details-panel__empty row-details-panel__empty--compact">
                <v-icon size="28" color="grey lighten-1">mdi-link-off</v-icon>
                <strong>No linked records</strong>
                <span>Connections created for this row will appear here.</span>
              </div>
            </section>
          </div>

          <footer
            class="row-details-panel__footer"
            :class="{ 'row-details-panel__footer--actions': canEditRows && panelEditableFields.length }">
            <template v-if="bulkEditActive">
              <v-btn
                text
                small
                :disabled="bulkEditSaving"
                @click="cancelBulkEdit">
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                small
                :loading="bulkEditSaving"
                :disabled="!bulkEditHasChanges || bulkEditExceedsCap"
                @click="saveBulkEdit">
                <v-icon left small>mdi-content-save-outline</v-icon>
                Apply to {{ bulkEditRowCount }} {{ bulkEditRowCount === 1 ? 'Row' : 'Rows' }}
                <span v-if="bulkEditHasChanges">({{ bulkTouchedFields.length }})</span>
              </v-btn>
            </template>
            <template v-else-if="canEditRows && rowDetailsEditableFields.length">
              <v-btn
                text
                small
                :disabled="rowDetailsIsSaving"
                @click="discardAndCloseRowDetails">
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                small
                :loading="rowDetailsIsSaving"
                :disabled="!rowDetailsHasChanges"
                @click="saveRowDetails">
                <v-icon left small>mdi-content-save-outline</v-icon>
                Save changes
                <span v-if="rowDetailsHasChanges">({{ getModifiedFieldsCount() }})</span>
              </v-btn>
            </template>
            <template v-else>
              <v-icon size="16" color="primary">mdi-information-outline</v-icon>
              <span>Double-click another row to inspect it without changing your selection.</span>
            </template>
          </footer>
        </aside>
      </transition>
    </div>

    <!-- Export Dialog -->
    <export-dialog
      v-model="showExportDialog"
      :register="currentRegister"
      :selected-row-ids="selectedRowIdsArray"
      :current-filters="currentFiltersForExport"
      :scope-params="exportScopeParams"
      :table-columns="tableColumns"
      :export-mode="exportMode"
      :total-rows="totalItems"
      @complete="onExportComplete" />

  </v-sheet>
</template>

<script>
  import { mapActions } from 'vuex';
  import ColumnsVisibilityControl from '@Share/components/ColumnsVisibilityControl.vue';
  import ExportDialog from '../components/admin/ExportDialog.vue';
  import registerService from '../services/registerService';
  import { registerReadContext } from '../modules/registerImportContracts';
  import { EventBus, REGISTER_EVENTS } from '@/event-bus';
  import { isAbortError } from '@/utils/isAbortError';
  import { FIELD_TYPES } from '@/enums/columnMappings.js';
  import { API_LIMITS } from '@/constants/apiLimits';
  import { ExportMode } from '../config/enums';
  import { RELATIONSHIP_ATTENTION_CLASSIFICATION, registerOwnershipScope } from '../config/relationshipUi';
  import { toRelationshipProblem } from '../services/registerRelationshipService';
  import LinkedTagChips from '../components/table/LinkedTagChips.vue';
  import RegisterTableStateNotice from '../components/table/RegisterTableStateNotice.vue';
  import {
    ALL_FACILITIES_FILTER,
    FACILITY_ASSIGNMENT_MODE,
    FACILITY_FILTER_KIND,
    facilityFilterItems,
    facilityFilterLabel,
    facilityFilterQueryParams,
    facilitySummaryChips,
    formatCount,
    isFacilityFilterActive,
    normalizeFacilityFilter,
    normalizeFacilitySummary,
    registerReadScopeFields,
    registerScopeHint,
    resolveRowFacility,
    sameFacilityFilter,
  } from '../modules/registerTableOwnership';
  import {
    LEFT_PINNED_COLUMNS,
    REGISTER_ROW_ACTION,
    REGISTER_TABLE_COLUMN,
    linkedTagOpensNewTab,
    orderRegisterTableHeaders,
    registerRowActions,
    resolvePinLayout,
    tagTypeIcon,
  } from '../modules/registerTableLayout';
  import {
    TABLE_LOAD_STATE,
    classifyTableLoadError,
    columnValuesLoadFailure,
    loadFailureClipboardText,
    registerDetailsClipboardText,
  } from '../modules/registerTableStates';
  import { $formatDate, $has } from '@/utils';
  import { TAG_COLOR_HEX } from '@/scripts/js/forgeModels.js';
  import { PANEL_IDS } from '@/constants/panelIds';
  import HttpService from '@Utils/httpService';
  import { log, warn, error } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';

  const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const GUID_IN_TEXT_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
  // One-time flash for rows a `?rowSearch` deep link points at. Slightly longer than the
  // CSS pulse so the animation always finishes before the class is dropped.
  const ROW_SEARCH_FLASH_MS = 2400;
  // How many rejected rows a failed bulk edit names in its toast before summarizing the rest.
  const BULK_EDIT_REPORTED_FAILURES = 3;
  // The route layout that hosts the Tag panel (layouts/view.vue, name 'viewer-layout').
  const TAG_PANEL_LAYOUT = 'viewer-layout';
  const ROW_DETAILS_HIDDEN_FIELDS = new Set([
    'id',
    'registerid',
    'facilityid',
    'facilitygroupid',
    'facilityreference',
    'haspiddocument',
    'linkedtags',
    'linkedrecords',
    'registerlinks',
    'entitylinks',
    'relationshiplinks',
    'discriminator',
    'targetid',
    'tagid',
    'relationshipid',
    'runid',
    'linkid',
    'scopeid',
    'targetkey',
    'rowversion',
    'hash',
  ]);
  const ROW_DETAILS_VISIBLE_SYSTEM_FIELDS = new Set([
    'rowindex',
    'tagvalue',
    'identityvalue',
    'isvalid',
    'facilityname',
    'createdate',
    'updatedate',
  ]);
  // DATA_TABLE_STANDARD: zebra stripes only once a table is this wide.
  const ZEBRA_MIN_COLUMNS = 12;
  // Pinned column widths (px). The same numbers size the `register-col--*` cells in the styles below;
  // the live widths are re-measured from the header before the pin budget is applied.
  const PINNED_COLUMN_WIDTH = Object.freeze({
    [REGISTER_TABLE_COLUMN.SELECT]: 50,
    [REGISTER_TABLE_COLUMN.ROW_INDEX]: 88,
    [REGISTER_TABLE_COLUMN.LINKED_TAGS]: 288,
    [REGISTER_TABLE_COLUMN.FACILITY]: 184,
    [REGISTER_TABLE_COLUMN.ACTIONS]: 112,
  });
  const PINNED_COLUMN_CLASS = Object.freeze({
    [REGISTER_TABLE_COLUMN.SELECT]: 'register-col--select',
    [REGISTER_TABLE_COLUMN.ROW_INDEX]: 'register-col--index',
    [REGISTER_TABLE_COLUMN.LINKED_TAGS]: 'register-col--links',
    [REGISTER_TABLE_COLUMN.FACILITY]: 'register-col--facility',
    [REGISTER_TABLE_COLUMN.ACTIONS]: 'register-col--actions',
  });
  const HEADERS_WITHOUT_FILTER = Object.freeze([
    REGISTER_TABLE_COLUMN.ACTIONS,
    REGISTER_TABLE_COLUMN.LINKED_TAGS,
    REGISTER_TABLE_COLUMN.ROW_INDEX,
    REGISTER_TABLE_COLUMN.FACILITY,
  ]);
  const GATE_STATE = Object.freeze({
    CONFIGURATION_REQUIRED: 'configurationRequired',
    FORBIDDEN: 'forbidden',
    FAILED: 'failed',
    WAITING_FOR_FIRST_IMPORT: 'waitingForFirstImport',
    EMPTY: 'empty',
  });
  const COPIED_NOTICE_MS = 2500;
  const ROW_MENU_ACTION_META = Object.freeze({
    [REGISTER_ROW_ACTION.HISTORY]: { icon: 'mdi-history', label: () => 'View row history' },
    [REGISTER_ROW_ACTION.VIEW_3D]: {
      icon: 'mdi-cube-outline',
      label: item => `View ${item.LinkedTags.length} ${item.LinkedTags.length === 1 ? 'tag' : 'tags'} in 3D`,
    },
    [REGISTER_ROW_ACTION.PID]: { icon: 'mdi-file-cad', label: () => 'View P&ID' },
  });
  const ROW_LINK_COLLECTIONS = Object.freeze([
    Object.freeze({ keys: ['LinkedTags', 'linkedTags'], kind: 'Tag', icon: 'mdi-tag-outline' }),
    Object.freeze({ keys: ['LinkedRecords', 'linkedRecords'], kind: 'Record', icon: 'mdi-table-row' }),
    Object.freeze({ keys: ['RegisterLinks', 'registerLinks'], kind: 'Record', icon: 'mdi-table-row' }),
    Object.freeze({ keys: ['EntityLinks', 'entityLinks'], kind: 'Record', icon: 'mdi-link-variant' }),
    Object.freeze({ keys: ['RelationshipLinks', 'relationshipLinks'], kind: 'Record', icon: 'mdi-link-variant' }),
  ]);

  export default {
    name: 'RegistersRegisterView',
    components: {
      ColumnsVisibilityControl,
      ExportDialog,
      LinkedTagChips,
      RegisterTableStateNotice,
    },
    props: {
      viewContext: { type: String, default: null, validator: value => ['central', 'member'].includes(value) },
      /**
       * The register as the list API returns it (RegisterVM: name, facilityGroupId / facilityGroup,
       * facilityAssignmentMode). table-data carries no mode and nothing at all on a failed load, so
       * the not-configured states, the scope hint and "Copy register details" read it from here.
       */
      registerInfo: {
        type: Object,
        default: null,
      },
      currentRegisterType: {
        type: String,
        required: true,
      },
      registerId: {
        type: [String, Number],
        default: null,
      },
      facilityId: {
        type: [String, Number],
        default: null,
      },
      initialSelectedRowIds: {
        type: Array,
        default: () => [],
      },
      relationshipAttentionFilter: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        loading: false,
        exporting: false,
        tableData: null,
        tableColumns: [],
        tableRows: [],
        // TABLE_LOAD_STATE of the latest read; dataLoadError keeps the raw server text for the clipboard only.
        dataLoadState: TABLE_LOAD_STATE.OK,
        dataLoadError: null,
        TABLE_LOAD_STATE,
        GATE_STATE,
        REGISTER_TABLE_COLUMN,
        HEADERS_WITHOUT_FILTER,
        ZEBRA_MIN_COLUMNS,
        // Central Separated Facility filter (sent as facilityIds / includeUnassigned).
        facilityFilter: { ...ALL_FACILITIES_FILTER },
        // Live widths behind the 70% pin budget (wrapper width + pinned header widths).
        pinMeasure: { viewportWidth: 0, widths: {} },
        copiedNotice: '',
        copiedNoticeTimer: null,
        pidMenuPosition: { x: 0, y: 0 },
        rowDetails: null,
        availableColumns: [],
        searchFilter: '',
        totalItems: 0,
        debounceTimer: null,
        currentRequestController: null, // AbortController for the in-flight table fetch (latest-wins)
        tableRequestGeneration: 0,
        lastAppliedFilterSnapshot: null,
        lastLoadedRegisterId: null, // Track the last loaded register ID
        attentionFilterProblem: null,
        suppressNextOptionsReload: false,
        is3dOpened: false, // Track viewer state

        // `?rowSearch` deep link (tag Records tab): lowercased identity value, live only
        // for the flash window. Never holds the user's search text - that stays in searchFilter.
        rowSearchHighlight: '',
        rowSearchHighlightTimer: null,

        // Edit mode properties
        editingRowId: null,
        editingData: {},
        originalData: {},
        modifiedFields: [],
        savingRows: [],

        // Bulk edit mode. Reuses editingData as the draft buffer, but tracks touched
        // fields separately and stickily: in bulk mode a field the user typed into and
        // then emptied is a deliberate clear, so it must stay in the patch even though
        // its value matches the untouched baseline.
        bulkEditActive: false,
        bulkEditRowIds: [],
        bulkTouchedFields: [],
        bulkEditSaving: false,

        // Column filter properties
        columnFilters: {}, // { columnName: { type: 'search|select', value: 'searchTerm'|selectedValue } }
        activeFilterMenu: null, // Currently open filter menu column
        tempFilterValue: '', // Temporary filter value while menu is open
        tempFilterType: 'search', // 'search' or 'select'
        tempSelectedValue: null, // Temporary selected value for select filters
        autocompleteSearch: '', // Search input for autocomplete
        columnFilterValues: {}, // { columnName: [array of unique values] }
        columnFilterNotes: {}, // { columnName: fixed note when its values could not be read }
        loadingFilterValues: new Set(), // Track which columns are loading filter values

        // Row selection (persists across pagination)
        selectedRowIds: new Set(),
        selectAllOnPage: false,

        // Export dialog
        showExportDialog: false,
        exportMode: ExportMode.Filtered,
        ExportMode, // Make available to template

        // Search debounce timer
        searchDebounceTimer: null,

        // Sort index toggle state
        togglingSortIndex: new Set(), // Track columns currently toggling sort index
        permissions,

        // P&ID menu state
        showPIdMenu: false,
        pidMenuDocuments: [],
        pidMenuTargetRow: null,
        pidMenuMatchedTagId: null,
        pidLoadingRowId: null,
      };
    },
    computed: {
      options: {
        get() {
          return this.$store.getters['REGISTERS/GET_OPTIONS'];
        },
        set(val) {
          this.$store.commit('REGISTERS/SET_OPTIONS', val);
        },
      },
      currentRegisterId() {
        // Use prop first, fallback to route parameter
        return this.registerId || this.$route.params.registerId;
      },
      currentFacilityId() {
        // Explicit management null must survive; owner IDs still support navigation.
        return Object.prototype.hasOwnProperty.call(this.$options.propsData || {}, 'facilityId')
          ? this.facilityId : this.$route.params.id;
      },
      tableReadContext() {
        return registerReadContext({
          viewContext: this.viewContext,
          facilityId: this.facilityId,
          hasFacilityProp: Object.prototype.hasOwnProperty.call(this.$options.propsData || {}, 'facilityId'),
          routeFacilityId: this.$route.params.id,
        });
      },
      activeRelationshipAttentionFilter() {
        const filter = this.relationshipAttentionFilter;
        if (typeof filter?.relationshipRunId !== 'string'
          || !filter.relationshipRunId.trim()
          || filter.classification !== RELATIONSHIP_ATTENTION_CLASSIFICATION) return null;
        return {
          relationshipRunId: filter.relationshipRunId,
          classification: RELATIONSHIP_ATTENTION_CLASSIFICATION,
        };
      },
      hasRelationshipAttentionFilter() { return Boolean(this.activeRelationshipAttentionFilter); },
      relationshipAttentionFilterKey() {
        const filter = this.activeRelationshipAttentionFilter;
        return filter ? `${filter.relationshipRunId}:${filter.classification}` : null;
      },
      attentionRunLabel() {
        const runId = this.activeRelationshipAttentionFilter?.relationshipRunId || '';
        return runId.length > 18 ? `${runId.slice(0, 10)}...${runId.slice(-6)}` : runId;
      },
      attentionFilterProblemTitle() {
        if (this.attentionFilterProblem?.status === 501) return 'Attention filtering is unavailable for this register';
        if (this.attentionFilterProblem?.status === 403) return 'You no longer have access to this relationship run';
        return 'Relationship attention rows could not be loaded';
      },
      registerName() {
        return this.tableData?.registerName || this.registerInfo?.name || '';
      },
      schemaUpdatedAt() {
        return this.tableData?.schemaUpdatedAt || null;
      },
      registerScope() {
        return this.registerInfo ? registerOwnershipScope(this.registerInfo) : null;
      },
      isFacilityGroupRegister() {
        return this.registerScope?.kind === 'facilityGroup';
      },
      facilityGroupName() {
        return this.registerInfo?.facilityGroupName || this.registerInfo?.facilityGroup?.name || '';
      },
      assignmentMode() {
        return this.registerInfo?.facilityAssignmentMode || null;
      },
      // Only a register object that actually carries the field may be called "mode not set".
      assignmentModeKnownUnset() {
        return this.isFacilityGroupRegister
          && Object.prototype.hasOwnProperty.call(this.registerInfo, 'facilityAssignmentMode')
          && !this.registerInfo.facilityAssignmentMode;
      },
      currentFacilityName() {
        const facilityId = this.currentFacilityId;
        return facilityId ? (this.facilityDict[facilityId] || '') : '';
      },
      facilitySummary() {
        if (this.viewContext !== 'central') return null;
        return normalizeFacilitySummary(this.tableData?.facilitySummary);
      },
      // Central view of a Separated Facility Group register: the API's summary says so, or the
      // register's own mode does (older API without a summary still gets the column).
      showFacilityColumn() {
        return this.viewContext === 'central'
          && (Boolean(this.facilitySummary) || this.assignmentMode === FACILITY_ASSIGNMENT_MODE.SEPARATED);
      },
      facilityChips() {
        if (!this.showFacilityColumn || this.dataLoadState !== TABLE_LOAD_STATE.OK) return null;
        return facilitySummaryChips(this.facilitySummary);
      },
      facilityFilterMenuItems() {
        if (!this.showFacilityColumn || this.dataLoadState !== TABLE_LOAD_STATE.OK) return [];
        return facilityFilterItems(this.facilitySummary, this.facilityFilter);
      },
      facilityFilterButtonLabel() {
        return facilityFilterLabel(this.facilityFilter, this.facilitySummary);
      },
      facilityFilterActive() {
        return this.showFacilityColumn && isFacilityFilterActive(this.facilityFilter);
      },
      unassignedFilterActive() {
        return this.facilityFilterActive && this.facilityFilter.kind === FACILITY_FILTER_KIND.UNASSIGNED;
      },
      facilityQueryParams() {
        return this.showFacilityColumn ? facilityFilterQueryParams(this.facilityFilter) : {};
      },
      scopeHint() {
        if (this.dataLoadState !== TABLE_LOAD_STATE.OK || !this.tableData || this.tableData.setupRequired) return null;
        const members = this.registerInfo?.facilityGroup?.facilities;
        return registerScopeHint({
          viewContext: this.viewContext,
          mode: this.isFacilityGroupRegister ? this.assignmentMode : null,
          groupName: this.facilityGroupName,
          facilityName: this.currentFacilityName,
          memberFacilityCount: Array.isArray(members) ? members.length : null,
        });
      },
      centralModeUnsetNoticeVisible() {
        return this.viewContext === 'central'
          && this.assignmentModeKnownUnset
          && this.dataLoadState === TABLE_LOAD_STATE.OK
          && this.totalItems > 0;
      },
      // Which card the grid shows when it has no rows (see the no-data slot).
      gateState() {
        if (this.dataLoadState === TABLE_LOAD_STATE.CONFIGURATION_REQUIRED) return GATE_STATE.CONFIGURATION_REQUIRED;
        if (this.dataLoadState === TABLE_LOAD_STATE.FORBIDDEN) return GATE_STATE.FORBIDDEN;
        if (this.dataLoadState === TABLE_LOAD_STATE.FAILED) return GATE_STATE.FAILED;
        if (this.tableData?.setupRequired) return GATE_STATE.WAITING_FOR_FIRST_IMPORT;
        if (this.viewContext === 'central'
          && this.assignmentModeKnownUnset
          && this.tableData
          && this.totalItems === 0
          && !this.hasActiveFilters
          && !this.facilityFilterActive
          && !this.hasRelationshipAttentionFilter) return GATE_STATE.WAITING_FOR_FIRST_IMPORT;
        return GATE_STATE.EMPTY;
      },
      // Visible columns in the enforced order: select, #, Linked Tags, Facility | ... | Actions.
      orderedHeaders() {
        const visibleCols = this.availableColumns
          .filter((col) => !col.hidden)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((col) => ({
            text: col.text,
            value: col.value,
            sortable: col.sortable !== false,
            width: col.width || 'auto',
            numeric: this.isColumnType(col, FIELD_TYPES.NUMBER),
          }))
          // The synthetic Facility column replaces a schema FacilityName column, never duplicates it.
          .filter(col => !(this.showFacilityColumn && col.value === 'FacilityName'));
        // An imported field is often called "Facility" too (the raw value, e.g. "Merch."); name the
        // resolved column differently so the grid never shows two "Facility" headers (AVA-602 U9).
        const importedFacilityField = visibleCols.some(col =>
          String(col.text || '').trim().toLowerCase() === 'facility');
        const facilityHeader = this.showFacilityColumn
          ? {
            text: importedFacilityField ? 'Assigned Facility' : 'Facility',
            value: REGISTER_TABLE_COLUMN.FACILITY,
            sortable: false,
            width: PINNED_COLUMN_WIDTH[REGISTER_TABLE_COLUMN.FACILITY],
            numeric: false,
          }
          : null;
        return orderRegisterTableHeaders(visibleCols, facilityHeader);
      },
      pinLayout() {
        const { widths, viewportWidth } = this.pinMeasure;
        const present = new Set(this.orderedHeaders.map(header => header.value));
        const widthOf = key => widths[key] || PINNED_COLUMN_WIDTH[key] || 0;
        return resolvePinLayout({
          leftColumns: LEFT_PINNED_COLUMNS
            .filter(key => present.has(key))
            .map(key => ({ key, width: widthOf(key) })),
          rightWidth: present.has(REGISTER_TABLE_COLUMN.ACTIONS) ? widthOf(REGISTER_TABLE_COLUMN.ACTIONS) : 0,
          viewportWidth,
        });
      },
      pinStyle() {
        return this.pinLayout.left.reduce((style, pin, index) => ({
          ...style,
          [`--register-pin-l${index}`]: `${pin.offset}px`,
        }), {});
      },
      visibleHeaders() {
        const layout = this.pinLayout;
        return this.orderedHeaders.map(({ numeric, ...header }) => {
          const classes = [];
          if (PINNED_COLUMN_CLASS[header.value]) classes.push(PINNED_COLUMN_CLASS[header.value]);
          const pinIndex = layout.left.findIndex(pin => pin.key === header.value);
          if (pinIndex >= 0) {
            classes.push('register-pin', `register-pin--l${pinIndex}`);
            if (layout.edgeKey === header.value) classes.push('register-pin--edge');
          }
          if (header.value === REGISTER_TABLE_COLUMN.ACTIONS && layout.pinRight) {
            classes.push('register-pin', 'register-pin--right');
          }
          const cellClasses = numeric ? [...classes, 'register-cell--number'] : classes;
          return { ...header, class: classes.join(' '), cellClass: cellClasses.join(' ') };
        });
      },
      defaultColumns() {
        // Build columns from the API response
        const columns = [];
        let order = 0;

        // Add selection column first
        columns.push({
          text: '',
          value: 'data-table-select',
          hidden: false,
          width: 50,
          sortable: false,
          order: order++,
          hidable: false,
        });

        // Add actions column
        columns.push({
          text: 'Actions',
          value: 'actions',
          hidden: false,
          width: PINNED_COLUMN_WIDTH[REGISTER_TABLE_COLUMN.ACTIONS],
          sortable: false,
          order: order++,
          hidable: false,
        });

        // Add columns from tableColumns
        this.tableColumns.forEach((col) => {
          columns.push({
            text: col.displayName || col.name,
            value: col.name,
            hidden: false,
            width: this.getColumnWidth(col),
            order: col.order || order++,
            hidable: true,
            dataType: col.dataType,
            isSystemColumn: col.isSystemColumn,
            isSortIndexed: col.isSortIndexed || false,
          });
        });

        return columns;
      },
      customColumns() {
        return this.availableColumns.filter(
          (col) => !['actions', 'RowIndex', 'LinkedTags', 'tag', 'data-table-select'].includes(col.value),
        );
      },
      canEditRows() {
        return this.$has(permissions.Registers.Update);
      },
      // The API rejects both export routes without Registers.Export; hide the buttons instead.
      canExport() {
        return this.$has(permissions.Registers.Export);
      },
      rowDetailsTitle() {
        if (!this.rowDetails) return 'Record details';
        const rowNumber = this.rowDetails.RowIndex;
        if (rowNumber !== null && rowNumber !== undefined && rowNumber !== '') {
          return `Record ${rowNumber}`;
        }
        const visibleReference = [
          this.rowDetails.TagValue,
          this.rowDetails.tag,
          this.rowDetails.IdentityValue,
        ].find(value => value && !GUID_PATTERN.test(String(value).trim()));
        return visibleReference ? this.sanitizeEndUserText(visibleReference) : 'Record details';
      },
      rowDetailsFields() {
        if (!this.rowDetails) return [];
        return this.buildPanelFields(this.rowDetails);
      },
      rowDetailsEditableFields() {
        if (!this.canEditRows) return [];
        return this.rowDetailsFields.filter(field => field.editable);
      },
      rowDetailsReadOnlyFields() {
        return this.rowDetailsFields.filter(field => !field.editable || !this.canEditRows);
      },
      rowDetailsHasChanges() {
        return Boolean(this.rowDetails && this.isEditingRow(this.rowDetails.Id)
          && this.getModifiedFieldsCount() > 0);
      },
      rowDetailsIsSaving() {
        return Boolean(this.rowDetails && this.isSavingRow(this.rowDetails.Id));
      },
      // Panel-mode facades: the details panel renders either one row or a bulk draft.
      isRowPanelOpen() {
        return Boolean(this.rowDetails) || this.bulkEditActive;
      },
      bulkEditRowCount() {
        return this.bulkEditRowIds.length;
      },
      bulkEditFields() {
        if (!this.bulkEditActive || !this.canEditRows) return [];
        return this.buildPanelFields(null).filter(field => field.editable);
      },
      panelEditableFields() {
        return this.bulkEditActive ? this.bulkEditFields : this.rowDetailsEditableFields;
      },
      bulkEditHasChanges() {
        return this.bulkEditActive && this.bulkTouchedFields.length > 0;
      },
      bulkEditExceedsCap() {
        return this.bulkEditRowCount > API_LIMITS.MAX_BULK_REGISTER_ROW_UPDATE;
      },
      bulkEditCapMessage() {
        return `Bulk Edit Supports Up To ${API_LIMITS.MAX_BULK_REGISTER_ROW_UPDATE} Rows. `
          + `This selection has ${this.bulkEditRowCount} `
          + `${this.bulkEditRowCount === 1 ? 'Row' : 'Rows'}. Reduce the selection and reopen.`;
      },
      panelIsSaving() {
        return this.bulkEditActive ? this.bulkEditSaving : this.rowDetailsIsSaving;
      },
      panelEyebrow() {
        return this.bulkEditActive ? 'Bulk edit' : 'Register record';
      },
      panelTitle() {
        if (!this.bulkEditActive) return this.rowDetailsTitle;
        return `Editing ${this.bulkEditRowCount} ${this.bulkEditRowCount === 1 ? 'Row' : 'Rows'}`;
      },
      rowDetailsOwner() {
        if (!this.rowDetails || !this.showFacilityColumn) return null;
        return this.rowOwner(this.rowDetails);
      },
      rowDetailsLinks() {
        if (!this.rowDetails) return [];
        const links = [];
        ROW_LINK_COLLECTIONS.forEach((collection) => {
          const sourceKey = collection.keys.find(key => Array.isArray(this.rowDetails[key]));
          if (!sourceKey) return;
          this.rowDetails[sourceKey].forEach((item, index) => {
            links.push(this.presentRowLink(item, collection, index));
          });
        });
        return links;
      },
      // Row selection computed properties
      selectedRowIdsArray() {
        return Array.from(this.selectedRowIds);
      },
      selectedRowCount() {
        return this.selectedRowIds.size;
      },
      hasSelectedRows() {
        return this.selectedRowIds.size > 0;
      },
      // Check if all rows on current page are selected
      allPageRowsSelected() {
        if (this.tableRows.length === 0) return false;
        return this.tableRows.every(row => this.selectedRowIds.has(row.Id));
      },
      // Check if some but not all rows on current page are selected
      somePageRowsSelected() {
        if (this.tableRows.length === 0) return false;
        const selectedCount = this.tableRows.filter(row => this.selectedRowIds.has(row.Id)).length;
        return selectedCount > 0 && selectedCount < this.tableRows.length;
      },
      // Check if any filter is active (search, column filters, or the central Facility filter)
      hasActiveFilters() {
        const query = this.buildDataQuery();
        return Boolean(query.search || Object.keys(query.columnFilters || {}).length || this.facilityFilterActive);
      },
      // Read scope every export follows, built like the table-data params: the view context, the
      // member Facility (the API rejects a member export without it) and the central Facility filter.
      exportScopeParams() {
        const { viewContext, facilityId } = this.tableReadContext;
        return {
          viewContext,
          ...(facilityId ? { facilityId } : {}),
          ...(this.facilityQueryParams || {}),
        };
      },
      // Read scope of the column filter values (GET column-values, API a4929ece): the table-data
      // view context and member Facility only. The central Facility filter is not a param there.
      columnValuesReadScope() {
        return registerReadScopeFields(this.tableReadContext);
      },
      // Cached filter values belong to one register and one read scope.
      columnValuesScopeKey() {
        const { viewContext = '', facilityId = '' } = this.columnValuesReadScope;
        return `${this.currentRegisterId || ''}|${viewContext}|${facilityId}`;
      },
      // Get current filters as backend format
      currentFiltersForExport() {
        const filters = {};
        Object.entries(this.columnFilters).forEach(([column, filterData]) => {
          if (filterData && filterData.value) {
            filters[column] = filterData.value;
          }
        });
        return filters;
      },
      // Current register object for export dialog
      currentRegister() {
        return {
          id: this.currentRegisterId,
          name: this.registerName,
        };
      },
      facilityDict() {
        return this.$store.getters.getFacilityDict || {};
      },
    },
    watch: {
      // Watch for column visibility changes - filter values will load on-demand
      availableColumns: {
        handler() {
          // Filter values will be loaded on-demand when user interacts with select filters
        },
        deep: true,
      },
      // Pinned widths and the scroll edges depend on the rendered columns and rows.
      orderedHeaders() {
        this.$nextTick(() => this.attachTableObservers());
      },
      tableRows() {
        this.$nextTick(() => this.attachTableObservers());
      },
      // Values read for another register or read scope must not be offered in this one.
      columnValuesScopeKey() {
        this.clearColumnFilterValueCache();
      },
      options: {
        handler() {
          if (this.suppressNextOptionsReload) {
            this.suppressNextOptionsReload = false;
            return;
          }
          // Only reload if we have a registerId and we're not in the middle of a reset
          if (this.lastLoadedRegisterId) {
            this.debouncedLoadTableData();
          }
        },
        deep: true,
      },
      // Watch for route changes to reload data when navigating between registers
      $route: {
        handler(newRoute, oldRoute) {
          const newId = newRoute.params.registerId;
          const oldId = oldRoute?.params?.registerId;
          const registerChanged = Boolean(newId) && (!oldId || newId !== oldId);
          // Deep links from the tag Records tab carry `?rowSearch`. A query-only change on
          // the register already open must re-seed and re-flash, so it runs through the SAME
          // ordered sequence as a register switch (reset -> seed -> one load) rather than a
          // second watcher that would race the reset.
          const rowSearch = this.readRowSearchQuery(newRoute);
          const rowSearchChanged = Boolean(newId) && Boolean(rowSearch)
            && rowSearch !== this.readRowSearchQuery(oldRoute);

          log('Route changed - New ID:', newId, 'Old ID:', oldId, 'rowSearch:', rowSearch);

          if (!registerChanged && !rowSearchChanged) return;

          log(registerChanged ? 'Loading new register data for:' : 'Re-seeding row search for:', newId);
          this.applyRegisterRouteState({ reset: registerChanged, rowSearch });
        },
        immediate: true,
        deep: true,
      },
      // Watch for registerId prop changes
      registerId: {
        handler(newId, oldId) {
          log('RegisterId prop changed - New ID:', newId, 'Old ID:', oldId);

          if (newId && (!oldId || newId !== oldId)) {
            log('Loading new register data for prop:', newId);
            // Reset data and reload when register ID changes
            this.resetComponentData();
            this.$nextTick(() => {
              this.loadTableData();
            });
          }
        },
        immediate: true,
      },
      initialSelectedRowIds: {
        handler(rowIds) {
          const nextSelection = new Set(rowIds || []);
          const unchanged = nextSelection.size === this.selectedRowIds.size
            && Array.from(nextSelection).every(id => this.selectedRowIds.has(id));
          if (!unchanged) this.selectedRowIds = nextSelection;
        },
        immediate: true,
      },
      relationshipAttentionFilter: {
        handler(newFilter, oldFilter) {
          const newKey = this.attentionFilterKeyFor(newFilter);
          const oldKey = this.attentionFilterKeyFor(oldFilter);
          if (newKey === oldKey) return;
          this.attentionFilterProblem = null;
          this.clearSelection();
          this.suppressNextOptionsReload = true;
          this.options = { ...this.options, page: 1 };
          this.$nextTick(() => {
            this.suppressNextOptionsReload = false;
            if (this.currentRegisterId) this.loadTableData();
          });
        },
        deep: true,
      },
    },
    methods: {
      $formatDate,
      $has,

      attentionFilterKeyFor(filter) {
        if (typeof filter?.relationshipRunId !== 'string'
          || !filter.relationshipRunId.trim()
          || filter.classification !== RELATIONSHIP_ATTENTION_CLASSIFICATION) return null;
        return `${filter.relationshipRunId}:${filter.classification}`;
      },

      openRowDetails(mouseEvent, rowContext) {
        const item = rowContext?.item;
        if (!item || this.isInteractiveTableTarget(mouseEvent?.target)) return;
        if (this.bulkEditActive) {
          if (this.bulkEditSaving) return;
          if (this.bulkEditHasChanges) {
            this.$dialog.notify.warning('Apply or cancel the bulk edit before opening a record.');
            return;
          }
          this.cancelBulkEdit();
        }
        const switchingRows = this.editingRowId && this.editingRowId !== item.Id;
        if (switchingRows && this.modifiedFields.length > 0) {
          this.$dialog.notify.warning('Save or cancel the open record before editing another row.');
          return;
        }
        if (switchingRows) this.cancelRowEdit();
        // Release the outgoing panel owner before initializing the incoming draft.
        // startRowEdit refuses to build a draft while a different row owns the panel;
        // that guard is there to block the inline pencil, not this sanctioned handover.
        if (this.rowDetails && String(this.rowDetails.Id) !== String(item.Id)) this.rowDetails = null;
        if (this.canEditRows && !this.isEditingRow(item.Id)) {
          // Initialize the draft before assigning the panel owner. startRowEdit
          // deliberately protects an already-open panel from being overwritten.
          if (!this.startRowEdit(item)) return;
        }
        this.rowDetails = item;
        this.$nextTick(() => this.focusRowDetailsPanel());
      },

      focusRowDetailsPanel() {
        const panel = this.$refs.rowDetailsPanel;
        if (!panel || typeof panel.focus !== 'function') return;
        try {
          panel.focus({ preventScroll: true });
        } catch (err) {
          panel.focus();
        }
      },

      requestCloseRowDetails() {
        if (this.bulkEditActive) {
          if (this.bulkEditSaving) return;
          if (this.bulkEditHasChanges) {
            this.$dialog.notify.warning('Apply your changes or choose Cancel before closing bulk edit.');
            return;
          }
          this.cancelBulkEdit();
          return;
        }
        if (this.rowDetailsHasChanges) {
          this.$dialog.notify.warning('Save your changes or choose Cancel before closing this record.');
          return;
        }
        this.closeRowDetails();
      },

      closeRowDetails() {
        if (this.rowDetails && this.isEditingRow(this.rowDetails.Id)) this.cancelRowEdit();
        this.rowDetails = null;
      },

      discardAndCloseRowDetails() {
        this.cancelRowEdit();
        this.rowDetails = null;
      },

      async saveRowDetails() {
        if (!this.rowDetails || !this.rowDetailsHasChanges) return;
        await this.saveRowEdit(this.rowDetails, { keepEditing: true, notifySuccess: true });
      },

      isPanelFieldModified(fieldName) {
        if (this.bulkEditActive) return this.bulkTouchedFields.includes(fieldName);
        return Boolean(this.rowDetails && this.isFieldModified(this.rowDetails, fieldName));
      },

      panelOriginalValue(field) {
        const formatted = this.formatRowDetailsValue(this.originalData[field.key], field.column);
        return formatted.value;
      },

      isInteractiveTableTarget(target) {
        const element = target?.nodeType === 3 ? target.parentElement : target;
        if (!element || typeof element.closest !== 'function') return false;
        return Boolean(element.closest([
          'button',
          'a',
          'input',
          'textarea',
          'select',
          '[role="button"]',
          '[role="checkbox"]',
          '.v-input',
          '.v-menu__activator',
          '.v-simple-checkbox',
          '.edit-field-container',
        ].join(', ')));
      },

      // Builds the panel field list. `row` is null in bulk mode, where only the key,
      // label, column and editable flag matter - values start empty for every field.
      buildPanelFields(row) {
        return [...this.tableColumns]
          .sort((left, right) => (left.order || 0) - (right.order || 0))
          .filter(column => this.isVisibleRowDetailsField(column))
          .map((column) => {
            const key = column.name || column.value;
            const formatted = this.formatRowDetailsValue(row ? row[key] : null, column);
            return {
              key,
              label: column.displayName || column.text || this.humanizeTechnicalLabel(key),
              value: formatted.value,
              empty: formatted.empty,
              column,
              editable: column?.isSystemColumn !== true
                && this.customColumns.some(customColumn => customColumn.value === key),
            };
          });
      },

      isVisibleRowDetailsField(column) {
        const key = String(column?.name || column?.value || '').trim();
        if (!key) return false;
        const normalizedKey = key.toLowerCase();
        if (ROW_DETAILS_HIDDEN_FIELDS.has(normalizedKey)) return false;
        return column?.isSystemColumn !== true
          || ROW_DETAILS_VISIBLE_SYSTEM_FIELDS.has(normalizedKey);
      },

      formatRowDetailsValue(rawValue, column) {
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          return { value: 'Not provided', empty: true };
        }
        if (this.isColumnType(column, FIELD_TYPES.BOOLEAN) || typeof rawValue === 'boolean') {
          return { value: this.getBooleanValue(rawValue) ? 'Yes' : 'No', empty: false };
        }
        if (this.isColumnType(column, FIELD_TYPES.DATE)) {
          return { value: this.$formatDate(rawValue), empty: false };
        }
        if (this.isColumnType(column, FIELD_TYPES.NUMBER) || typeof rawValue === 'number') {
          return { value: this.formatNumber(rawValue), empty: false };
        }
        if (Array.isArray(rawValue)) {
          const values = rawValue
            .map(value => this.objectDisplayValue(value))
            .filter(Boolean);
          return values.length
            ? { value: values.join(', '), empty: false }
            : { value: 'Not provided', empty: true };
        }
        if (typeof rawValue === 'object') {
          const displayValue = this.objectDisplayValue(rawValue);
          return displayValue
            ? { value: displayValue, empty: false }
            : { value: 'Additional information available', empty: false };
        }
        return { value: this.sanitizeEndUserText(rawValue), empty: false };
      },

      objectDisplayValue(value) {
        if (value === null || value === undefined || value === '') return '';
        if (typeof value !== 'object') return this.sanitizeEndUserText(value);
        const candidate = [
          value.name,
          value.displayName,
          value.label,
          value.title,
          value.identityValue,
          value.tagValue,
          value.value,
        ].find(item => item !== null && item !== undefined && item !== '');
        return candidate === undefined ? '' : this.sanitizeEndUserText(candidate);
      },

      sanitizeEndUserText(value) {
        const text = String(value).trim();
        if (!text) return 'Not provided';
        if (GUID_PATTERN.test(text)) return 'Internal reference hidden';
        return text.replace(GUID_IN_TEXT_PATTERN, 'Internal reference hidden');
      },

      humanizeTechnicalLabel(value) {
        const text = String(value || '')
          .replace(/^Forge/, '')
          .replace(/Tag$/, '')
          .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          .replace(/[_-]+/g, ' ')
          .trim();
        if (!text || GUID_PATTERN.test(text) || /^\d+$/.test(text)) return '';
        return text
          .toLowerCase()
          .replace(/\b\w/g, character => character.toUpperCase());
      },

      presentRowLink(item, collection, index) {
        const source = item && typeof item === 'object' ? item : {};
        const labelCandidate = typeof item === 'string'
          ? item
          : [
            source.name,
            source.displayName,
            source.label,
            source.title,
            source.identityValue,
            source.tagValue,
          ].find(value => value !== null && value !== undefined && value !== '');
        const safeLabel = labelCandidate && !GUID_PATTERN.test(String(labelCandidate).trim())
          ? this.sanitizeEndUserText(labelCandidate)
          : `${collection.kind} ${index + 1}`;
        const typeCandidate = [
          source.displayType,
          source.typeLabel,
          source.targetKind,
          source.entityType,
          source.discriminator,
          source.type,
        ].find(value => value !== null && value !== undefined && value !== '');
        const friendlyType = this.humanizeTechnicalLabel(typeCandidate) || collection.kind;
        const internalKey = source.id || source.rowId || source.linkId || safeLabel;
        const targetId = this.safeLinkedRecordId(source.id || source.tagId || source.targetId);
        const navigationFacilityId = this.safeLinkedRecordId(source.navigationFacilityId);
        const opensNewTab = this.linkOpensNewTab(navigationFacilityId);
        return {
          key: `${collection.kind}-${internalKey}-${index}`,
          label: safeLabel,
          type: friendlyType === 'Base' ? collection.kind : friendlyType,
          icon: collection.icon,
          targetId,
          navigationFacilityId,
          clickable: collection.kind === 'Tag' && Boolean(targetId && navigationFacilityId),
          opensNewTab,
          facilityName: opensNewTab && navigationFacilityId ? ((this.facilityDict || {})[navigationFacilityId] || '') : '',
        };
      },

      // Only the viewer layout (layouts/view.vue) hosts the Tag panel. Anywhere else (Register
      // Management) a same-Facility "Opens here" did nothing, so the Tag opens in its Facility in
      // a new tab (AVA-602 U12).
      linkOpensNewTab(navigationFacilityId) {
        if (!navigationFacilityId) return false;
        if (this.$route?.meta?.layout !== TAG_PANEL_LAYOUT) return true;
        return linkedTagOpensNewTab(this.safeLinkedRecordId(this.currentFacilityId), navigationFacilityId);
      },

      safeLinkedRecordId(value) {
        if (typeof value !== 'string') return null;
        const normalized = value.trim();
        return GUID_PATTERN.test(normalized) ? normalized : null;
      },

      async openLinkedRecord(link) {
        const targetId = this.safeLinkedRecordId(link?.targetId);
        if (!targetId) return;
        if (this.rowDetailsIsSaving) return;
        if (this.rowDetailsHasChanges) {
          this.$dialog.notify.warning('Save or cancel your record changes before opening a linked record.');
          return;
        }
        const navigationFacilityId = this.safeLinkedRecordId(link?.navigationFacilityId);
        if (!navigationFacilityId) {
          this.$dialog.notify.warning('This linked record has no available Facility destination.');
          return;
        }
        try {
          // Same rule the chips and the details list use to show the new-tab icon.
          if (this.linkOpensNewTab(navigationFacilityId)) {
            // Open the Tag in its authorized Facility in a new tab so the register view stays open.
            const route = this.$router.resolve({
              name: 'facility',
              params: { id: navigationFacilityId },
              query: { tagId: targetId, openPanel: 'true' },
            });
            window.open(route.href, '_blank', 'noopener');
            return;
          }

          const tag = await this.$store.dispatch('GET_BASE_TAG_BY_ID', {
            id: targetId,
            assetId: navigationFacilityId,
            expectedKind: null,
          });
          if (!tag) {
            this.$dialog.notify.error('The linked record is no longer available.');
            return;
          }
          this.$store.commit('SET_EDITING_TAG', tag);
          await this.$store.dispatch('GENERAL/OPEN_PANEL', PANEL_IDS.TAGS);
        } catch (err) {
          error('RegisterTable', 'Failed to open linked tag:', err);
          this.$dialog.notify.error('The linked record could not be opened.');
        }
      },

      async onLinkedTagSaved({ tagId } = {}) {
        if (!tagId) return;
        const normalizedTagId = String(tagId).toLowerCase();
        const containsLinkedTag = this.tableRows.some((row) => {
          const linkedTags = Array.isArray(row.LinkedTags)
            ? row.LinkedTags
            : (Array.isArray(row.linkedTags) ? row.linkedTags : []);
          return linkedTags.some((tag) => {
            const linkedTagId = tag && typeof tag === 'object' ? (tag.id ?? tag.tagId) : tag;
            return linkedTagId != null && String(linkedTagId).toLowerCase() === normalizedTagId;
          });
        });
        if (containsLinkedTag) await this.loadTableData();
      },

      isBooleanHeader(columnValue) {
        const col = this.availableColumns.find((c) => c.value === columnValue);
        if (!col) return false;
        return this.isColumnType(col, 4);
      },

      ...mapActions('REGISTERS', {
        exportRegisterAction: 'EXPORT_REGISTER',
        openAuditSidebarRow: 'OPEN_AUDIT_SIDEBAR_ROW',
        bulkUpdateRegisterRowsAction: 'BULK_UPDATE_REGISTER_ROWS',
      }),

      /**
       * Handle search filter changes with debounce
       */
      onSearchFilterChange() {
        // Clear any pending search
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
        }
        this.invalidateFilterSnapshot();

        // Debounce the search to avoid excessive API calls
        this.searchDebounceTimer = setTimeout(() => {
          // Reset to first page when searching
          this.options.page = 1;
          this.loadTableData();
        }, 400);
      },

      /**
       * Normalized `?rowSearch` deep-link value for a route. Only meaningful on the consumer
       * register route - an embedded table (admin workspace) must ignore a stray query param.
       * Repeated query params arrive as arrays; only the first one is meaningful.
       */
      readRowSearchQuery(route) {
        if (!route?.params?.registerId) return '';
        const raw = route.query?.rowSearch;
        const value = Array.isArray(raw) ? raw[0] : raw;
        return typeof value === 'string' ? value.trim() : '';
      },

      /**
       * Seed the existing server-side search from a `?rowSearch` deep link and mark the
       * matching rows for a one-time flash. Writes `searchFilter` only - never
       * `options.search`, which SET_OPTIONS merges and resetComponentData() cannot clear.
       * The 400ms search debounce is deliberately skipped: the value is known at
       * navigation time, so the caller issues one load instead.
       */
      seedRowSearch(value) {
        log('Seeding row search from route query:', value, 'register:', this.currentRegisterId);
        // A deep link supersedes whatever the user was mid-typing; without this the pending
        // debounce would fire a second load moments after the seeded one.
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = null;
        }
        this.searchFilter = value;
        this.clearRowSearchHighlight();
        this.rowSearchHighlight = value.toLowerCase();
        // Land on page 1 without letting the options watcher fire its own debounced reload.
        this.suppressNextOptionsReload = true;
        this.options = { ...this.options, page: 1 };
      },

      clearRowSearchHighlight() {
        if (this.rowSearchHighlightTimer) {
          clearTimeout(this.rowSearchHighlightTimer);
          this.rowSearchHighlightTimer = null;
        }
        this.rowSearchHighlight = '';
      },

      rowMatchesRowSearchHighlight(item) {
        if (!this.rowSearchHighlight) return false;
        const identity = item?.IdentityValue ?? item?.identityValue;
        if (identity === null || identity === undefined) return false;
        return String(identity).trim().toLowerCase() === this.rowSearchHighlight;
      },

      /**
       * Flash the deep-linked rows once, then drop the highlight state only - the seeded
       * search text stays so the user can see and edit what was searched for.
       */
      armRowSearchHighlight() {
        if (!this.rowSearchHighlight) return;
        const matched = this.tableRows.filter(row => this.rowMatchesRowSearchHighlight(row)).length;
        log(
          'Row search highlight applied - seed:',
          this.rowSearchHighlight,
          'matched rows:',
          matched,
          'of',
          this.tableRows.length,
        );
        if (this.rowSearchHighlightTimer) clearTimeout(this.rowSearchHighlightTimer);
        this.rowSearchHighlightTimer = setTimeout(() => {
          this.rowSearchHighlightTimer = null;
          this.rowSearchHighlight = '';
        }, ROW_SEARCH_FLASH_MS);
      },

      /**
       * The one ordered entry point for register/deep-link changes: reset (only when the
       * register changed) -> seed `?rowSearch` -> a single load. Both the route watcher and
       * activated() go through here; activated() runs after the watcher and would otherwise
       * reset a freshly seeded search away.
       */
      async applyRegisterRouteState({ reset, rowSearch }) {
        if (reset) this.resetComponentData();
        if (rowSearch) this.seedRowSearch(rowSearch);
        // Let the options watcher consume the suppression flag before the single load runs.
        await this.$nextTick();
        if (rowSearch) this.suppressNextOptionsReload = false;
        await this.loadTableData();
      },

      buildDataQuery() {
        const rawSearch = this.options?.search || this.searchFilter || '';
        const search = String(rawSearch).trim();
        const columnFilters = {};
        Object.entries(this.columnFilters || {}).forEach(([column, filterData]) => {
          const rawValue = filterData?.value;
          if (rawValue === null || rawValue === undefined) return;
          const value = String(rawValue).trim();
          if (value === '') return;
          columnFilters[column] = value;
        });
        return {
          ...(search ? { search } : {}),
          ...(Object.keys(columnFilters).length ? { columnFilters } : {}),
        };
      },

      emitFilterSnapshot({ generation, status, dataQuery, totalCount = null }) {
        if (status === 'ready') {
          const query = dataQuery || {};
          const columnFilters = { ...(query.columnFilters || {}) };
          const relationshipAttentionFilter = this.activeRelationshipAttentionFilter
            ? { ...this.activeRelationshipAttentionFilter }
            : null;
          this.lastAppliedFilterSnapshot = {
            generation,
            requestId: `data-${generation}`,
            status: 'ready',
            search: query.search || '',
            columnFilters,
            relationshipAttentionFilter,
            // The Facility filter narrows the grid but is not part of this snapshot's query, which
            // relationship runs reuse. Fail closed: no count, so no "filtered rows" scope is offered.
            totalCount: this.facilityFilterActive ? null : totalCount,
            hasFilters: Boolean(
              query.search || Object.keys(columnFilters).length || relationshipAttentionFilter
            ),
          };
          this.$emit('filter-snapshot-change', { ...this.lastAppliedFilterSnapshot });
          return;
        }

        const query = dataQuery || {};
        const columnFilters = { ...(query.columnFilters || {}) };
        const relationshipAttentionFilter = this.activeRelationshipAttentionFilter
          ? { ...this.activeRelationshipAttentionFilter }
          : null;
        this.$emit('filter-snapshot-change', {
          generation,
          requestId: `data-${generation}`,
          status,
          search: query.search || '',
          columnFilters,
          relationshipAttentionFilter,
          totalCount: null,
          hasFilters: Boolean(
            query.search || Object.keys(columnFilters).length || relationshipAttentionFilter
          ),
        });
      },

      invalidateFilterSnapshot() {
        // A query edited during the debounce window must immediately retire the
        // older fetch. Otherwise that older response could briefly advertise a
        // result count beside a newer search value that has not run yet.
        if (this.currentRequestController) {
          this.currentRequestController.abort();
          this.currentRequestController = null;
        }
        const generation = ++this.tableRequestGeneration;
        this.emitFilterSnapshot({
          generation,
          status: 'loading',
          dataQuery: this.buildDataQuery(),
        });
      },

      resetComponentData() {
        log('Resetting component data');
        // Reset all component data to initial state
        this.tableData = null;
        this.tableColumns = [];
        this.tableRows = [];
        this.rowDetails = null;
        this.cancelBulkEdit();
        this.availableColumns = [];
        this.searchFilter = '';
        this.clearRowSearchHighlight();
        this.totalItems = 0;
        this.attentionFilterProblem = null;
        this.dataLoadState = TABLE_LOAD_STATE.OK;
        this.dataLoadError = null;
        this.facilityFilter = { ...ALL_FACILITIES_FILTER };
        this.lastLoadedRegisterId = null; // Also reset the last loaded ID
        this.selectedRowIds = new Set(this.initialSelectedRowIds || []);
        this.options = {
          page: 1,
          itemsPerPage: 50,
          sortBy: [],
          sortDesc: [],
        };

        // Clear any pending debounce timers
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = null;
        }

        // Abort any in-flight table request from the previous register
        if (this.currentRequestController) {
          this.currentRequestController.abort();
          this.currentRequestController = null;
        }

        // Reset column filters
        this.columnFilters = {};
        this.activeFilterMenu = null;
        this.tempFilterValue = '';
        this.tempFilterType = 'search';
        this.tempSelectedValue = null;
        this.autocompleteSearch = '';
        this.clearColumnFilterValueCache();
        this.lastAppliedFilterSnapshot = null;
        const generation = ++this.tableRequestGeneration;
        this.emitFilterSnapshot({
          generation,
          status: 'idle',
          dataQuery: {},
        });
      },

      async loadTableData() {
        // Get the current register ID from prop or route
        const currentRegisterId = this.currentRegisterId;

        // Check if registerId exists
        if (!currentRegisterId) {
          warn('No register ID provided');
          return;
        }

        // Abort any in-flight request; the newest request wins. The active controller
        // doubles as the "latest request" token for the guards below. (Previously this
        // early-returned on `this.loading`, silently dropping the newest search/page/
        // sort/filter reload while an earlier request was still loading.)
        if (this.currentRequestController) {
          this.currentRequestController.abort();
        }
        const controller = new AbortController();
        this.currentRequestController = controller;
        const requestGeneration = ++this.tableRequestGeneration;
        const requestFacilityId = this.currentFacilityId;
        const requestAttentionFilter = this.activeRelationshipAttentionFilter;
        const requestAttentionFilterKey = this.relationshipAttentionFilterKey;
        const dataQuery = this.buildDataQuery();

        this.emitFilterSnapshot({
          generation: requestGeneration,
          status: 'loading',
          dataQuery,
        });

        log('Loading table data for register:', currentRegisterId);
        this.loading = true;
        try {
          // Build query parameters
          const params = {
            page: this.options.page,
            itemsPerPage: this.options.itemsPerPage === -1 ? 999999 : this.options.itemsPerPage,
            viewContext: this.tableReadContext.viewContext,
          };
          if (this.tableReadContext.facilityId) params.facilityId = this.tableReadContext.facilityId;
          if (requestAttentionFilter) {
            params.relationshipRunId = requestAttentionFilter.relationshipRunId;
            params.classification = requestAttentionFilter.classification;
          }
          // Central Separated Facility filter: facilityIds / includeUnassigned (registerTableOwnership).
          Object.assign(params, this.facilityQueryParams || {});

          // Apply the same data query that is exposed to transient preview scope.
          if (dataQuery.search) params.search = dataQuery.search;

          // Add sorting
          if (this.options.sortBy.length > 0) {
            params.sortBy = this.options.sortBy[0];
            params.sortDesc = this.options.sortDesc[0];
          }

          // Add column filters - convert to backend format
          if (dataQuery.columnFilters) params.columnFilters = { ...dataQuery.columnFilters };

          // Fetch table data from API using the current register ID
          const response = await registerService.getRegisterTableData(currentRegisterId, params, controller.signal);

          // A response whose network already returned cannot be stopped by abort(),
          // so guard at commit time: only the latest request may mutate shared state.
          if (this.currentRequestController !== controller
            || String(this.currentRegisterId) !== String(currentRegisterId)
            || String(this.currentFacilityId || '') !== String(requestFacilityId || '')
            || this.relationshipAttentionFilterKey !== requestAttentionFilterKey) return;

          this.tableData = response;
          this.tableColumns = response.columns || [];
          this.tableRows = response.rows || [];
          if (this.rowDetails && !this.rowDetailsHasChanges && !this.rowDetailsIsSaving) {
            const refreshedRow = this.tableRows.find(row => String(row.Id) === String(this.rowDetails.Id));
            if (refreshedRow) {
              this.rowDetails = refreshedRow;
              if (this.canEditRows) this.startRowEdit(refreshedRow, { allowPanelOwner: true });
            } else {
              this.closeRowDetails();
            }
          }
          this.totalItems = response.totalCount || 0;
          this.attentionFilterProblem = null;
          this.dataLoadState = TABLE_LOAD_STATE.OK;
          this.dataLoadError = null;
          this.emitFilterSnapshot({
            generation: requestGeneration,
            status: 'ready',
            dataQuery,
            totalCount: this.totalItems,
          });

          // Track the loaded register ID
          this.lastLoadedRegisterId = currentRegisterId;
          log('Successfully loaded data for register:', currentRegisterId);

          // Rows are on screen now, so the deep-link flash can start its one-time window.
          this.armRowSearchHighlight();

          // Initialize columns if not already done
          if (this.availableColumns.length === 0) {
            this.initializeColumns();
          }

          // Update page title with register name
          if (this.tableData?.registerName) {
            document.title = `Envizion - ${this.tableData.registerName}`;
          }
        } catch (err) {
          // Ignore cancellations — expected when a newer request supersedes this one
          // (axios rejects aborts with CanceledError / code ERR_CANCELED).
          if (isAbortError(err)) {
            return;
          }
          // A superseded request that fails with a real (non-abort) error must not
          // blank the newer request's grid or raise a spurious toast — only the
          // latest request may surface an error.
          if (this.currentRequestController !== controller) {
            return;
          }
          if (String(this.currentRegisterId) !== String(currentRegisterId)
            || String(this.currentFacilityId || '') !== String(requestFacilityId || '')
            || this.relationshipAttentionFilterKey !== requestAttentionFilterKey) return;
          error('Failed to load register data:', err);
          // The deep-linked rows never arrived; drop the pending flash rather than let it
          // sit armed and fire on some unrelated load minutes later.
          this.clearRowSearchHighlight();
          if (requestAttentionFilter) {
            this.attentionFilterProblem = toRelationshipProblem(
              err,
              'The relationship attention rows could not be loaded.',
            );
          } else {
            // Reported inline by the grid, never by a toast: a global scream duplicates the
            // workspace's own failure panel and fires even when the user is on another tab.
            // Classified from the status and the typed error code only; the server's text is kept
            // for "Copy details" and never rendered.
            const failure = classifyTableLoadError(err);
            this.dataLoadState = failure.state;
            this.dataLoadError = failure;
            // A register that needs its mode set has no member rows to show: drop any stale page.
            if (failure.state === TABLE_LOAD_STATE.CONFIGURATION_REQUIRED) this.tableRows = [];
          }
          this.emitFilterSnapshot({
            generation: requestGeneration,
            status: 'error',
            dataQuery,
          });
          // Rows already on screen stay readable; only a load with nothing to show empties out.
          if (!this.tableRows.length) this.totalItems = 0;
        } finally {
          // Clear the controller only if this request still owns it, and release the
          // spinner when idle (a superseded request must not lock it on).
          if (this.currentRequestController === controller) {
            this.currentRequestController = null;
          }
          if (this.currentRequestController === null) {
            this.loading = false;
          }
        }
      },

      debouncedLoadTableData() {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.loadTableData();
        }, 500);
      },

      clearRelationshipAttentionFilter() {
        this.$emit('clear-relationship-attention-filter');
      },

      initializeColumns() {
        this.availableColumns = [...this.defaultColumns];
      },

      getColumnWidth(column) {
        const widthMap = {
          RowIndex: PINNED_COLUMN_WIDTH[REGISTER_TABLE_COLUMN.ROW_INDEX],
          LinkedTags: PINNED_COLUMN_WIDTH[REGISTER_TABLE_COLUMN.LINKED_TAGS], // chips wrap inside a fixed cell
          // Legacy string-based types (for compatibility)
          date: 150,
          boolean: 100,
          number: 180,
          text: 150,
          // Numeric FIELD_TYPES enum values
          1: 250, // TEXT
          2: 180, // NUMBER
          3: 150, // DATE
          4: 200, // BOOLEAN
        };

        return widthMap[column.name] || widthMap[column.dataType] || 150;
      },

      isColumnType(column, fieldType) {
        // Support both legacy string types and numeric FIELD_TYPES
        if (typeof column.dataType === 'number') {
          return column.dataType === fieldType;
        }

        // Legacy compatibility
        const typeMap = {
          text: FIELD_TYPES.TEXT,
          number: FIELD_TYPES.NUMBER,
          date: FIELD_TYPES.DATE,
          boolean: FIELD_TYPES.BOOLEAN,
        };

        return typeMap[column.dataType] === fieldType;
      },

      // Register column metadata has no length or multiline hint (DataType is only
      // text/number/date/boolean), so every free-text column edits in an auto-growing
      // textarea: short codes stay one line, long findings/recommendations show in full.
      // Key columns stay single-line so Enter cannot put a line break into a matched value.
      isMultilineTextColumn(column) {
        return this.isColumnType(column, FIELD_TYPES.TEXT)
          && column.isIdentityColumn !== true
          && !column.linkEntityType;
      },

      formatDate(dateString) {
        if (!dateString) return '';

        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        } catch (error) {
          return dateString;
        }
      },

      formatNumber(value) {
        if (value === null || value === undefined || value === '') return '—';

        // Check if it's a valid number
        const num = Number(value);
        if (isNaN(num)) return value;

        // Format with thousand separators
        return num.toLocaleString('en-US');
      },

      async exportRegister() {
        this.exporting = true;
        const currentRegisterId = this.currentRegisterId;
        try {
          const blob = await this.exportRegisterAction({
            registerId: currentRegisterId,
          });

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.registerName}_export.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$dialog.notify.success('Register exported successfully');
        } catch (error) {
          error('Export failed:', error);
          this.$dialog.notify.error('Failed to export register');
        } finally {
          this.exporting = false;
        }
      },

      viewIn3D() {
        // Navigate to 3D viewer with register context
        const currentRegisterId = this.currentRegisterId;
        this.$store.commit('SET_VIEWER_TYPE', '3d');
        this.$router.push({
          name: 'MainViewer',
          query: { registerId: currentRegisterId },
        });
      },

      getRowClass(item) {
        const classes = [];

        // Add editing class if row is being edited
        if (this.isInlineEditingRow(item.Id)) {
          classes.push('editing-row');
        }

        if (this.rowDetails?.Id && this.rowDetails.Id === item.Id) {
          classes.push('details-row');
        }

        // One-time flash for the row a `?rowSearch` deep link pointed at
        if (this.rowMatchesRowSearchHighlight(item)) {
          classes.push('row-search-flash');
        }

        return classes.join(' ');
      },

      // Edit mode methods
      startRowEdit(item, { allowPanelOwner = false } = {}) {
        if (!this.canEditRows || !item || this.isSavingRow(item.Id)) return false;
        if (this.rowDetails && String(this.rowDetails.Id) === String(item.Id) && !allowPanelOwner) {
          return true;
        }
        if (this.rowDetails && String(this.rowDetails.Id) !== String(item.Id)) {
          this.$dialog.notify.warning('Save or cancel the open record before editing another row.');
          return false;
        }
        this.editingRowId = item.Id;
        this.editingData = {};
        this.originalData = {};
        this.modifiedFields = [];

        // Initialize editing data with current values and store originals
        this.customColumns.forEach((column) => {
          const rawValue = item[column.value];
          let value = '';

          // Handle boolean fields - convert true/false to Yes/No
          if (this.isColumnType(column, 4)) {
            const stringValue = String(rawValue).toLowerCase().trim();
            if (rawValue === true || stringValue === 'true' || stringValue === 'yes') {
              value = true; // v-select expects boolean for Yes/No
            } else if (rawValue === false || stringValue === 'false' || stringValue === 'no') {
              value = false; // v-select expects boolean for Yes/No
            } else {
              value = false; // Default to No
            }
          }
          // Handle date fields - convert to YYYY-MM-DD format for HTML5 date input
          else if (this.isColumnType(column, 3) && rawValue) {
            try {
              // Handle different date formats and avoid timezone issues
              let dateStr = rawValue.toString().trim();

              // If it's in MM/DD/YYYY format, convert it properly
              if (dateStr.includes('/')) {
                const parts = dateStr.split('/');
                if (parts.length === 3) {
                  const month = parts[0].padStart(2, '0');
                  const day = parts[1].padStart(2, '0');
                  const year = parts[2];
                  value = `${year}-${month}-${day}`;
                } else {
                  value = dateStr;
                }
              } else {
                // For ISO or other formats, parse carefully
                const date = new Date(rawValue);
                if (!isNaN(date.getTime())) {
                  // Use local date components to avoid timezone shift
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  value = `${year}-${month}-${day}`;
                } else {
                  value = rawValue;
                }
              }
            } catch (e) {
              warn('Failed to parse date:', rawValue, e);
              value = rawValue;
            }
          } else {
            value = rawValue ?? '';
          }

          this.$set(this.editingData, column.value, value);
          this.$set(this.originalData, column.value, value);
        });
        return true;
      },

      // Draft value -> API value for one column. Shared by the single-row and bulk saves.
      // Boolean editors already hold real booleans; keep that type so relationship sync can
      // validate and copy the value without treating the display labels as stored data.
      convertFieldValueForApi(column, newValue) {
        let valueToSend = newValue;

        // Convert date fields back to proper format for API
        if (this.isColumnType(column, 3) && newValue) {
          try {
            // If it's in YYYY-MM-DD format from the date input
            if (newValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              // Create a date in local timezone to avoid shifts
              const [year, month, day] = newValue.split('-');
              const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              valueToSend = date.toISOString();
            } else {
              // Fallback for other formats
              const date = new Date(newValue);
              if (!isNaN(date.getTime())) {
                valueToSend = date.toISOString();
              } else {
                valueToSend = newValue;
              }
            }
          } catch (e) {
            warn('Failed to convert date for API:', newValue, e);
            valueToSend = newValue;
          }
        }

        return valueToSend;
      },

      async saveRowEdit(item, { keepEditing = false, notifySuccess = false } = {}) {
        if (!this.editingRowId || this.isSavingRow(item.Id)) return false;

        this.savingRows = [...this.savingRows, item.Id];
        let saved = false;
        let resetEditingBaseline = false;

        try {
          // Send only changed fields. The API merges this patch into the current row,
          // which prevents a refreshed panel from overwriting unrelated newer values.
          const rowData = {};
          let hasChanges = false;

          for (const column of this.customColumns) {
            const newValue = this.editingData[column.value];
            const originalValue = this.originalData[column.value];

            if (newValue === originalValue) continue;
            hasChanges = true;

            // Include only the modified field in the patch request.
            rowData[column.value] = this.convertFieldValueForApi(column, newValue);
          }

          // Only make request if there are actual changes
          if (hasChanges) {
            // Add TagValue to rowData for backend identification
            if (item.tag) {
              rowData.TagValue = item.tag;
            }

            log('Sending changed row fields:', rowData);

            // Call the register service with the changed-field patch.
            await registerService.updateRegisterRow(item.Id, rowData);

            // Update local data after successful save
            for (const [fieldName, value] of Object.entries(rowData)) {
              this.$set(item, fieldName, value);
            }
            saved = true;
            if (notifySuccess) this.$dialog.notify.success('Record changes saved.');
          } else {
            log('No changes detected, skipping save request');
            saved = true;
          }

          if (keepEditing) resetEditingBaseline = true;
          else this.cancelRowEdit();
          // Note: Filter values will refresh on-demand if user interacts with filters again
        } catch (err) {
          error('Failed to update register row:', err);
          this.$dialog.notify.error('Failed to update register row');
          // Don't update local data on error - keep original values
        } finally {
          this.savingRows = this.savingRows.filter(rowId => rowId !== item.Id);
          // Rebuild the edit baseline only after the row leaves its saving state.
          // startRowEdit intentionally rejects initialization while a save is active.
          if (saved && resetEditingBaseline) {
            this.startRowEdit(item, { allowPanelOwner: true });
          }
        }
        return saved;
      },

      cancelRowEdit() {
        this.editingRowId = null;
        this.editingData = {};
        this.originalData = {};
        this.modifiedFields = [];
      },

      // ==========================================
      // BULK EDIT METHODS
      // ==========================================

      editSelectedRows() {
        if (!this.canEditRows) return;
        if (!this.hasSelectedRows) {
          this.$dialog.notify.warning('No rows selected for editing');
          return;
        }
        if (this.rowDetailsIsSaving) return;
        if (this.rowDetailsHasChanges) {
          this.$dialog.notify.warning('Save or cancel the open record before editing the selected rows.');
          return;
        }
        this.startBulkEdit(this.selectedRowIdsArray);
      },

      startBulkEdit(rowIds) {
        const editableFields = this.buildPanelFields(null).filter(field => field.editable);
        if (!editableFields.length) {
          this.$dialog.notify.warning('This register has no editable fields');
          return;
        }

        // Both modes share editingData as their draft buffer, so drop the single-row
        // draft and the panel it belongs to before building the bulk one.
        this.cancelRowEdit();
        this.rowDetails = null;
        this.editingData = {};
        this.originalData = {};
        this.bulkTouchedFields = [];

        // null is the untouched baseline: it renders as an empty control and no field
        // type can produce it from user input, so it never collides with a real value.
        editableFields.forEach((field) => {
          this.$set(this.editingData, field.key, null);
          this.$set(this.originalData, field.key, null);
        });

        this.bulkEditRowIds = [...rowIds];
        this.bulkEditActive = true;
        this.$nextTick(() => this.focusRowDetailsPanel());
      },

      cancelBulkEdit() {
        // Bail when no bulk draft is open: editingData is shared with the single-row
        // editor and must not be wiped out from under it.
        if (!this.bulkEditActive) return;
        this.bulkEditActive = false;
        this.bulkEditRowIds = [];
        this.bulkTouchedFields = [];
        this.editingData = {};
        this.originalData = {};
      },

      // Only fields the user actually touched reach the patch, and only when they are
      // real editable columns. Untouched fields are never sent, so the selected rows
      // keep their current values for everything the user left alone.
      buildBulkPatch() {
        const patch = {};
        for (const column of this.customColumns) {
          if (!this.bulkTouchedFields.includes(column.value)) continue;
          patch[column.value] = this.convertFieldValueForApi(column, this.editingData[column.value]);
        }
        return patch;
      },

      async saveBulkEdit() {
        if (!this.bulkEditActive || this.bulkEditSaving || this.bulkEditExceedsCap) return;

        const changes = this.buildBulkPatch();
        if (!Object.keys(changes).length) return;

        const rowIds = [...this.bulkEditRowIds];
        if (!rowIds.length) return;

        this.bulkEditSaving = true;
        try {
          log('Applying bulk row patch to', rowIds.length, 'rows:', changes);
          const result = await this.bulkUpdateRegisterRowsAction({
            registerId: this.currentRegisterId,
            rowIds,
            changes,
          });

          // The server applies the batch as one transaction, so a success response means
          // every listed row took the patch and none was left half edited.
          const savedCount = result?.updatedCount ?? rowIds.length;
          this.applyBulkPatchToLoadedRows(changes, rowIds);
          this.$dialog.notify.success(`Saved ${savedCount} ${savedCount === 1 ? 'Row' : 'Rows'}.`);
          this.cancelBulkEdit();
        } catch (err) {
          error('Failed to bulk update register rows:', err);
          this.notifyBulkEditFailure(err);
        } finally {
          this.bulkEditSaving = false;
        }
      },

      // Patches the rows currently on screen. Selected rows on other pages are left
      // alone - they pick the new values up from the server on their next fetch.
      applyBulkPatchToLoadedRows(patch, savedRowIds) {
        if (!savedRowIds.length) return;
        const savedRowIdSet = new Set(savedRowIds.map(rowId => String(rowId)));
        this.tableRows.forEach((row) => {
          if (!savedRowIdSet.has(String(row.Id))) return;
          for (const [fieldName, value] of Object.entries(patch)) {
            this.$set(row, fieldName, value);
          }
        });
      },

      // A rejected batch wrote nothing, so the message names the rows to fix rather than
      // reporting a partial save. The panel stays open with the draft intact for a retry.
      notifyBulkEditFailure(err) {
        const failures = err?.response?.data?.errors;
        if (Array.isArray(failures) && failures.length) {
          const detail = failures
            .slice(0, BULK_EDIT_REPORTED_FAILURES)
            .map(failure => `${failure.rowId}: ${failure.reason}`)
            .join(' | ');
          const remaining = failures.length - BULK_EDIT_REPORTED_FAILURES;
          this.$dialog.notify.error(
            `No rows were saved. ${failures.length} ${failures.length === 1 ? 'Row' : 'Rows'} rejected — `
            + `${detail}${remaining > 0 ? ` (+${remaining} more)` : ''}`,
          );
          return;
        }
        this.$dialog.notify.error(
          err?.response?.data?.error || 'No rows were saved. The bulk update failed.',
        );
      },

      isEditingRow(itemId) {
        return this.editingRowId === itemId;
      },

      isInlineEditingRow(itemId) {
        return this.isEditingRow(itemId) && this.rowDetails?.Id !== itemId;
      },

      isSavingRow(itemId) {
        return this.savingRows.includes(itemId);
      },

      isFieldModified(item, fieldName) {
        if (!this.isEditingRow(item.Id)) return false;
        const currentValue = this.editingData[fieldName];
        const originalValue = this.originalData[fieldName];
        return currentValue !== originalValue;
      },

      getOriginalValue(item, fieldName) {
        return this.originalData[fieldName] || '-';
      },

      onFieldChange(fieldName, value) {
        if (this.bulkEditActive) {
          // Sticky on purpose: in bulk mode every field starts blank, so a field the
          // user typed into and then emptied still has to reach the patch as a clear.
          if (!this.bulkTouchedFields.includes(fieldName)) {
            this.bulkTouchedFields = [...this.bulkTouchedFields, fieldName];
          }
          return;
        }
        // Update the modified fields set
        const originalValue = this.originalData[fieldName];
        if (value !== originalValue) {
          if (!this.modifiedFields.includes(fieldName)) {
            this.modifiedFields = [...this.modifiedFields, fieldName];
          }
        } else {
          this.modifiedFields = this.modifiedFields.filter(item => item !== fieldName);
        }
      },

      getModifiedFieldsCount() {
        return this.modifiedFields.length;
      },

      getBooleanValue(value) {
        // Handle boolean values and text variations
        if (value === true) return true;
        if (value === false) return false;

        const stringValue = String(value).toLowerCase().trim();
        if (stringValue === 'true' || stringValue === 'yes') return true;
        if (stringValue === 'false' || stringValue === 'no') return false;

        // Default to false for any other value
        return false;
      },

      showRegistersOn3d(item) {
        // Show specific tags in 3D viewer
        let tags = [];

        if (Array.isArray(item.LinkedTags)) {
          tags = item.LinkedTags;
        }

        // Only proceed if we have valid tags
        if (tags.length === 0) {
          warn('No valid tags found for 3D viewing');
          this.$dialog.notify.warning('No tags available to view in 3D');
          return;
        }

        log('Opening 3D viewer with tags:', tags);
        this.$emit('showIn3d', tags);
      },

      /**
       * Isolate all linked tags from selected rows in the 3D viewer.
       * Collects LinkedTags from each selected row, deduplicates, and emits to parent.
       */
      isolateSelectedIn3d() {
        const selectedRows = this.tableRows.filter(row => this.selectedRowIds.has(row.Id));

        // Collect all LinkedTags from selected rows
        const allLinkedTags = selectedRows.flatMap(row =>
          Array.isArray(row.LinkedTags) ? row.LinkedTags : []
        );

        // Deduplicate by tag id
        const uniqueTags = [...new Map(allLinkedTags.map(t => [t.id, t])).values()];

        if (uniqueTags.length === 0) {
          this.$dialog.notify.warning('None of the selected rows have linked tags with 3D objects', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const rowsWithoutTags = selectedRows.filter(
          row => !Array.isArray(row.LinkedTags) || row.LinkedTags.length === 0
        ).length;

        if (rowsWithoutTags > 0) {
          this.$dialog.notify.info(`${rowsWithoutTags} row(s) without linked tags were skipped`, {
            position: 'top-right',
            timeout: 3000,
          });
        }

        log('Isolating selected in 3D:', uniqueTags.length, 'unique tags from', selectedRows.length, 'rows');
        this.$emit('showIn3d', uniqueTags);
      },

      /** One row's Linked Tags as chips; navigation goes through presentRowLink + openLinkedRecord. */
      linkedTagChips(tags) {
        if (!Array.isArray(tags)) return [];
        const collection = ROW_LINK_COLLECTIONS[0];
        return tags.map((tag, index) => {
          const link = this.presentRowLink(tag, collection, index);
          const typeLabel = link.type === collection.kind ? 'Tag' : link.type;
          const where = link.facilityName ? ` in ${link.facilityName}` : '';
          return {
            key: link.key,
            label: link.label,
            color: this.getDiscriminatorColor(tag?.discriminator),
            icon: tagTypeIcon(tag?.discriminator),
            clickable: link.clickable,
            opensNewTab: link.clickable && link.opensNewTab,
            tooltip: `${typeLabel}${link.facilityName ? ` · ${link.facilityName}` : ''} · Opens in new tab`,
            ariaLabel: !link.clickable
              ? `${link.label} (${typeLabel})`
              : (link.opensNewTab ? `Open ${link.label}${where} (opens in a new tab)` : `Open ${link.label}`),
            link,
          };
        });
      },

      linkAriaLabel(link) {
        if (!link.clickable) return `${link.label} details unavailable`;
        if (!link.opensNewTab) return `Open ${link.label} details`;
        return `Open ${link.label}${link.facilityName ? ` in ${link.facilityName}` : ''} (opens in a new tab)`;
      },

      linkSubtitle(link) {
        if (!link.clickable) return link.type;
        if (!link.opensNewTab) return `${link.type} · Opens here`;
        return [link.type, link.facilityName, 'Opens in new tab'].filter(Boolean).join(' · ');
      },

      /** Owning Facility of a row (central Separated Facility column and details panel). */
      rowOwner(item) {
        return resolveRowFacility(item, { summary: this.facilitySummary, facilityDict: this.facilityDict });
      },

      formatCount,

      rowMenuActions(item) {
        const { menu } = registerRowActions({
          canEdit: this.canEditRows,
          hasLinkedTags: Array.isArray(item?.LinkedTags) && item.LinkedTags.length > 0,
          hasPid: Boolean(item?.HasPIdDocument),
        });
        return menu.map(key => ({
          key,
          icon: ROW_MENU_ACTION_META[key].icon,
          label: ROW_MENU_ACTION_META[key].label(item),
        }));
      },

      /** Same handlers the always-visible icons used before they moved into the ⋮ menu. */
      runRowMenuAction(actionKey, item, event) {
        if (actionKey === REGISTER_ROW_ACTION.HISTORY) {
          this.openRowAuditHistory(item);
        } else if (actionKey === REGISTER_ROW_ACTION.VIEW_3D) {
          this.showRegistersOn3d(item);
        } else if (actionKey === REGISTER_ROW_ACTION.PID) {
          // The multi-document chooser opens where the menu item was (mouse or keyboard).
          const rect = event?.currentTarget?.getBoundingClientRect?.();
          this.pidMenuPosition = rect
            ? { x: rect.left, y: rect.bottom }
            : { x: event?.clientX || 0, y: event?.clientY || 0 };
          this.handlePIdClick(item);
        }
      },

      setFacilityFilter(filter) {
        const next = normalizeFacilityFilter(filter);
        if (sameFacilityFilter(next, this.facilityFilter)) return;
        this.facilityFilter = next;
        // Back to page 1 without letting the options watcher queue a second, debounced load.
        this.suppressNextOptionsReload = true;
        this.options = { ...this.options, page: 1 };
        this.$nextTick(() => {
          this.suppressNextOptionsReload = false;
          this.loadTableData();
        });
      },

      clearFacilityFilter() {
        this.setFacilityFilter(ALL_FACILITIES_FILTER);
      },

      toggleUnassignedFilter() {
        this.setFacilityFilter(this.unassignedFilterActive
          ? ALL_FACILITIES_FILTER
          : { kind: FACILITY_FILTER_KIND.UNASSIGNED });
      },

      async copyToClipboard(text, noticeKey) {
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
          await navigator.clipboard.writeText(text);
          this.copiedNotice = noticeKey;
          if (this.copiedNoticeTimer) clearTimeout(this.copiedNoticeTimer);
          this.copiedNoticeTimer = setTimeout(() => {
            this.copiedNotice = '';
            this.copiedNoticeTimer = null;
          }, COPIED_NOTICE_MS);
        } catch (err) {
          warn('RegisterTable', 'Copy to clipboard failed:', err);
          this.$dialog.notify.warning('Copy is not available in this browser.');
        }
      },

      copyRegisterDetails() {
        this.copyToClipboard(registerDetailsClipboardText({
          registerName: this.registerName,
          registerId: this.currentRegisterId,
          facilityGroupName: this.facilityGroupName,
          openedFrom: this.currentFacilityName || this.currentFacilityId || '',
          time: new Date().toISOString(),
        }), 'register');
      },

      copyFailureDetails() {
        const failure = this.dataLoadError || {};
        this.copyToClipboard(loadFailureClipboardText({
          registerName: this.registerName,
          registerId: this.currentRegisterId,
          view: this.viewContext === 'central' ? 'Central' : (this.currentFacilityName || this.currentFacilityId || ''),
          time: new Date().toISOString(),
          status: failure.status,
          code: failure.code,
          serverMessage: failure.serverMessage,
        }), 'failure');
      },

      // ---- Pinned columns: 70% budget + edge shadows ------------------------------------------

      tableWrapperElement() {
        return this.$refs.table?.$el?.querySelector?.('.v-data-table__wrapper') || null;
      },

      attachTableObservers() {
        const wrapper = this.tableWrapperElement();
        if (!wrapper || wrapper === this.pinWrapperEl) {
          this.schedulePinMeasure();
          return;
        }
        this.detachTableObservers();
        this.pinWrapperEl = wrapper;
        wrapper.addEventListener('scroll', this.updateScrollEdges, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          this.pinResizeObserver = new ResizeObserver(() => this.schedulePinMeasure());
          this.pinResizeObserver.observe(wrapper);
        }
        this.schedulePinMeasure();
      },

      detachTableObservers() {
        if (this.pinWrapperEl) this.pinWrapperEl.removeEventListener('scroll', this.updateScrollEdges);
        if (this.pinResizeObserver) this.pinResizeObserver.disconnect();
        if (this.pinMeasureFrame) cancelAnimationFrame(this.pinMeasureFrame);
        this.pinWrapperEl = null;
        this.pinResizeObserver = null;
        this.pinMeasureFrame = null;
      },

      schedulePinMeasure() {
        if (this.pinMeasureFrame || typeof requestAnimationFrame !== 'function') return;
        this.pinMeasureFrame = requestAnimationFrame(() => {
          this.pinMeasureFrame = null;
          this.measurePinnedColumns();
          this.updateScrollEdges();
        });
      },

      measurePinnedColumns() {
        const wrapper = this.pinWrapperEl;
        if (!wrapper) return;
        const cells = wrapper.querySelectorAll('thead > tr:first-child > th');
        const widths = {};
        this.visibleHeaders.forEach((header, index) => {
          if (PINNED_COLUMN_WIDTH[header.value] && cells[index]) widths[header.value] = cells[index].offsetWidth;
        });
        const viewportWidth = wrapper.clientWidth;
        const previous = this.pinMeasure;
        const unchanged = previous.viewportWidth === viewportWidth
          && Object.keys(widths).length === Object.keys(previous.widths).length
          && Object.keys(widths).every(key => previous.widths[key] === widths[key]);
        if (!unchanged) this.pinMeasure = { viewportWidth, widths };
      },

      /** Soft shadows on the pinned edges only while content actually scrolls under them. */
      updateScrollEdges() {
        const wrapper = this.pinWrapperEl;
        if (!wrapper) return;
        wrapper.classList.toggle('register-table__wrapper--scrolled', wrapper.scrollLeft > 0);
        wrapper.classList.toggle(
          'register-table__wrapper--more-right',
          wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth - 1,
        );
      },

      getDiscriminatorColor(discriminator) {
        // Map discriminator to TAG_COLOR_HEX values
        const colorMap = {
          ForgeEquipment: TAG_COLOR_HEX.Equipment,
          ForgePipeline: TAG_COLOR_HEX.Pipeline,
          ForgeStructural: TAG_COLOR_HEX.Structural,
          ForgeAnnotation: TAG_COLOR_HEX.General,
          ForgeScaffold: TAG_COLOR_HEX.Scaffold,
          ForgePermit: TAG_COLOR_HEX.Permit,
          ForgeContainer: TAG_COLOR_HEX.Container,
          ForgeInspection: TAG_COLOR_HEX.Inspection,
          ForgeAnomaly: TAG_COLOR_HEX.Anomaly,
        };
        return colorMap[discriminator] || TAG_COLOR_HEX.General;
      },

      /**
       * Opens the audit history sidebar for a specific row
       * @param {Object} item - The row data object
       */
      openRowAuditHistory(item) {
        if (!item || !item.Id) {
          warn('Cannot open audit history: invalid row data');
          return;
        }
        // Dispatch Vuex action to open sidebar in row mode
        this.openAuditSidebarRow({
          row: item,
          registerId: this.currentRegisterId
        });
      },

      // Column filter methods
      async toggleFilterMenu(columnValue, isOpen) {
        if (isOpen) {
          this.activeFilterMenu = columnValue;
          const existingFilter = this.columnFilters[columnValue];
          this.tempFilterType = existingFilter ? existingFilter.type : 'search';
          this.tempFilterValue = existingFilter && existingFilter.type === 'search' ? existingFilter.value : '';
          this.tempSelectedValue = existingFilter && existingFilter.type === 'select' ? existingFilter.value : null;
        } else {
          this.activeFilterMenu = null;
          this.tempFilterValue = '';
          this.tempFilterType = 'search';
          this.tempSelectedValue = null;
          this.autocompleteSearch = '';
        }
      },

      closeFilterMenu() {
        this.activeFilterMenu = null;
        this.tempFilterValue = '';
        this.tempFilterType = 'search';
        this.tempSelectedValue = null;
        this.autocompleteSearch = '';
      },

      hasColumnFilter(columnValue) {
        const filter = this.columnFilters[columnValue];
        return filter && filter.value && String(filter.value).trim() !== '';
      },

      getColumnFilterType(columnValue) {
        const filter = this.columnFilters[columnValue];
        return filter ? filter.type : 'search';
      },

      getColumnFilterValue(columnValue) {
        const filter = this.columnFilters[columnValue];
        return filter ? filter.value : '';
      },

      applyColumnFilter(columnValue) {
        let filterValue = null;
        let filterType = this.tempFilterType;

        if (filterType === 'search' && this.tempFilterValue && this.tempFilterValue.trim() !== '') {
          filterValue = this.tempFilterValue.trim();
        } else if (filterType === 'select' && this.tempSelectedValue) {
          filterValue = this.tempSelectedValue;
        }

        if (filterValue) {
          this.$set(this.columnFilters, columnValue, {
            type: filterType,
            value: filterValue
          });
        } else {
          this.$delete(this.columnFilters, columnValue);
        }
        this.invalidateFilterSnapshot();
        this.closeFilterMenu();
        this.debouncedLoadTableData();
      },

      clearColumnFilter(columnValue) {
        this.$delete(this.columnFilters, columnValue);
        this.invalidateFilterSnapshot();
        this.closeFilterMenu();
        this.debouncedLoadTableData();
      },

      clearAllFilters() {
        this.columnFilters = {};
        this.invalidateFilterSnapshot();
        this.debouncedLoadTableData();
      },

      // Row selection methods (persist across pagination)
      isRowSelected(row) {
        return this.selectedRowIds.has(row.Id);
      },

      toggleRowSelection(row) {
        if (this.selectedRowIds.has(row.Id)) {
          this.selectedRowIds.delete(row.Id);
        } else {
          this.selectedRowIds.add(row.Id);
        }
        // Trigger reactivity
        this.selectedRowIds = new Set(this.selectedRowIds);
        this.emitSelectionChange();
      },

      toggleSelectAllOnPage() {
        if (this.allPageRowsSelected) {
          // Deselect all rows on current page
          this.tableRows.forEach(row => {
            this.selectedRowIds.delete(row.Id);
          });
        } else {
          // Select all rows on current page
          this.tableRows.forEach(row => {
            this.selectedRowIds.add(row.Id);
          });
        }
        // Trigger reactivity
        this.selectedRowIds = new Set(this.selectedRowIds);
        this.emitSelectionChange();
      },

      clearSelection() {
        this.selectedRowIds = new Set();
        this.emitSelectionChange();
      },

      emitSelectionChange() {
        this.$emit('selection-change', Array.from(this.selectedRowIds));
      },

      // Export methods
      openExportDialog(mode) {
        if (!this.canExport) return;
        this.exportMode = mode;
        this.showExportDialog = true;
      },

      exportSelected() {
        if (this.selectedRowIds.size === 0) {
          this.$dialog.notify.warning('No rows selected for export');
          return;
        }
        this.openExportDialog(ExportMode.Selected);
      },

      exportFiltered() {
        this.openExportDialog(ExportMode.Filtered);
      },

      onExportComplete({ filename }) {
        this.$dialog.notify.success(`Export complete: ${filename}`);
        // Optionally clear selection after export
        // this.clearSelection();
      },

      onTempFilterChange(value) {
        log(`onTempFilterChange: ${value}`);
        // Optional: Add live filtering or validation here
      },

      onAutocompleteSelect(value) {
        // Called when user selects a value from autocomplete
        this.tempSelectedValue = value;
      },

      async loadColumnFilterValues(columnValue) {
        if (this.loadingFilterValues.has(columnValue) || this.isBooleanHeader(columnValue)) {
          return;
        }

        const scopeKey = this.columnValuesScopeKey;
        this.loadingFilterValues.add(columnValue);
        this.$delete(this.columnFilterNotes, columnValue);
        try {
          const values = await registerService.getColumnFilterValues(
            this.currentRegisterId, columnValue, this.columnValuesReadScope);
          if (this.columnValuesScopeKey !== scopeKey) return;
          this.$set(this.columnFilterValues, columnValue, values || []);
        } catch (err) {
          if (this.columnValuesScopeKey !== scopeKey) return;
          const failure = columnValuesLoadFailure(err);
          if (failure.retryable) error(`Failed to load filter values for column ${columnValue}:`, err);
          else warn(`Filter values for column ${columnValue} are not available (${failure.state})`);
          this.$set(this.columnFilterNotes, columnValue, failure.note);
          // A retryable failure stays uncached so choosing "Select" again asks once more.
          if (!failure.retryable) this.$set(this.columnFilterValues, columnValue, []);
        } finally {
          if (this.columnValuesScopeKey === scopeKey) this.loadingFilterValues.delete(columnValue);
        }
      },

      clearColumnFilterValueCache() {
        this.columnFilterValues = {};
        this.columnFilterNotes = {};
        this.loadingFilterValues.clear();
      },

      async onFilterTypeChange() {
        // Clear temp values when switching filter types
        this.tempFilterValue = '';
        this.tempSelectedValue = null;
        this.autocompleteSearch = '';

        // Only load filter values when user specifically selects 'select' type
        if (this.tempFilterType === 'select' && this.activeFilterMenu) {
          const columnValue = this.activeFilterMenu;
          if (!this.isBooleanHeader(columnValue) && !this.columnFilterValues[columnValue]) {
            await this.loadColumnFilterValues(columnValue);
          }
        }
      },

      // Helper method to refresh filter values for a specific column if needed
      // This can be called manually if filter values become stale
      async refreshColumnFilterValues(columnValue) {
        if (this.columnFilterValues[columnValue] && !this.isBooleanHeader(columnValue)) {
          // Clear cached values and they will reload on next user interaction
          delete this.columnFilterValues[columnValue];
        }
      },

      // ==========================================
      // SORT INDEX METHODS
      // ==========================================

      /**
       * Check if a column is a dynamic (non-system) column eligible for sort index toggle
       */
      isDynamicColumn(columnValue) {
        const col = this.tableColumns.find(c => c.name === columnValue);
        return col && !col.isSystemColumn;
      },

      /**
       * Check if a column has a sort index enabled
       */
      isColumnSortIndexed(columnValue) {
        const col = this.availableColumns.find(c => c.value === columnValue);
        return col && col.isSortIndexed;
      },

      /**
       * Toggle sort index on/off for a column
       */
      async toggleSortIndex(columnValue) {
        if (this.togglingSortIndex.has(columnValue)) return;

        const currentlyIndexed = this.isColumnSortIndexed(columnValue);
        this.togglingSortIndex.add(columnValue);
        this.togglingSortIndex = new Set(this.togglingSortIndex); // trigger reactivity

        try {
          if (currentlyIndexed) {
            await registerService.disableColumnSortIndex(this.currentRegisterId, columnValue);
          } else {
            await registerService.enableColumnSortIndex(this.currentRegisterId, columnValue);
          }

          // Update local column state
          const colIndex = this.availableColumns.findIndex(c => c.value === columnValue);
          if (colIndex !== -1) {
            this.$set(this.availableColumns, colIndex, {
              ...this.availableColumns[colIndex],
              isSortIndexed: !currentlyIndexed,
            });
          }

          // Also update tableColumns for consistency
          const tblCol = this.tableColumns.find(c => c.name === columnValue);
          if (tblCol) {
            tblCol.isSortIndexed = !currentlyIndexed;
          }

          this.$dialog.notify.success(
            currentlyIndexed
              ? `Fast sorting disabled for "${columnValue}"`
              : `Fast sorting enabled for "${columnValue}"`,
            { position: 'top-right', timeout: 3000 }
          );
        } catch (err) {
          error('Failed to toggle sort index:', err);
          this.$dialog.notify.error('Failed to update sort index', {
            position: 'top-right',
            timeout: 3000,
          });
        } finally {
          this.togglingSortIndex.delete(columnValue);
          this.togglingSortIndex = new Set(this.togglingSortIndex);
        }
      },

      // ==========================================
      // P&ID DOCUMENT METHODS
      // ==========================================

      /**
       * Handle P&ID button click - fetches docs for the row's linked tags
       * Button is only visible when server confirmed HasPIdDocument=true
       */
      async handlePIdClick(item) {
        this.pidLoadingRowId = item.Id;
        try {
          const pIdTypeId = this.$store.getters.GET_DOCUMENT_TYPES?.find(t => t.name === 'P&IDs')?.id;
          const currentAssetId = this.$store.state.assetId;
          const facility = this.$store.getters['FACILITY/GET_FACILITY'];
          const facilityGroupId = facility?.facilityGroupId || null;

          // Fetch docs for all linked tags in parallel
          const tagIds = (item.LinkedTags || []).map(t => t.id);
          const results = await Promise.allSettled(
            tagIds.map(tagId => HttpService.get({
              url: `/api/documents/TagId/${tagId}`,
              params: {
                AssetId: currentAssetId,
                ...(facilityGroupId && { FacilityGroupId: facilityGroupId }),
                ...(pIdTypeId && { DocumentTypeIds: [pIdTypeId] }),
              },
            }))
          );

          let allMarkups = [];
          let allAttachments = [];
          let matchedTagId = null;

          for (let i = 0; i < results.length; i++) {
            if (results[i].status === 'fulfilled') {
              const data = results[i].value.data;
              if (data?.markupsDocuments?.length || data?.attachments?.length) {
                if (!matchedTagId) matchedTagId = tagIds[i];
                allMarkups.push(...(data.markupsDocuments || []));
                allAttachments.push(...(data.attachments || []));
              }
            }
          }

          const allDocs = this.deduplicatePIdDocuments(allMarkups, allAttachments);

          if (allDocs.length === 0) {
            this.$dialog.notify.warning('No P&ID document found', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          this.pidMenuMatchedTagId = matchedTagId;

          if (allDocs.length === 1) {
            this.pidMenuTargetRow = item;
            await this.openSinglePIdDocument(allDocs[0], matchedTagId);
          } else {
            this.pidMenuDocuments = allDocs;
            this.pidMenuTargetRow = item;
            this.showPIdMenu = true;
          }
        } catch (err) {
          error('Error opening P&ID:', err);
          this.$dialog.notify.error('Failed to load P&ID document', {
            position: 'top-right',
            timeout: 3000,
          });
        } finally {
          this.pidLoadingRowId = null;
          // Reset menu state to prevent stale data on retry
          if (!this.showPIdMenu) {
            this.pidMenuDocuments = [];
            this.pidMenuTargetRow = null;
          }
        }
      },

      /**
       * Deduplicate P&ID documents from different sources
       * Prefers markupsDocuments over attachments when duplicates exist
       */
      deduplicatePIdDocuments(markupsDocuments, attachments) {
        const seen = new Set();
        const result = [];

        for (const doc of markupsDocuments) {
          if (!seen.has(doc.id)) {
            seen.add(doc.id);
            result.push(doc);
          }
        }

        for (const doc of attachments) {
          if (!seen.has(doc.id)) {
            seen.add(doc.id);
            result.push(doc);
          }
        }

        return result;
      },

      /**
       * Open a single P&ID document, handling cross-facility navigation
       */
      async openSinglePIdDocument(pIdDoc, tagId) {
        const currentAssetId = this.$store.state.assetId;
        const isOtherFacility = pIdDoc.assetId && pIdDoc.assetId !== currentAssetId;

        if (isOtherFacility) {
          this.openPIdInNewTab(pIdDoc.assetId, tagId, pIdDoc.id);
        } else {
          await this.openPIdInCurrentViewer(pIdDoc, tagId);
        }
      },

      /**
       * Open P&ID in the current facility's document viewer
       */
      async openPIdInCurrentViewer(pIdDoc, tagId) {
        this.$store.commit('setTagIdToHighlight', tagId);

        const documentId = pIdDoc.documentId || pIdDoc.id;

        if (!documentId) {
          warn('RegisterTable', 'openPIdInCurrentViewer: Document ID not available');
          this.$dialog.notify.warning('Document ID not available', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const result = await this.$store.dispatch('OPEN_DOCUMENT_IN_VIEWER', {
          document: {
            documentId: documentId,
            // Required: OPEN_DOCUMENT_IN_VIEWER signs this into the blob URL; omit it
            // and getDocumentBlobUrl returns null, so the doc silently never loads.
            // (|| url mirrors getDocumentBlobUrl's own fallback for attachment-sourced docs.)
            storageReference: pIdDoc.storageReference || pIdDoc.url,
            fileName: pIdDoc.fileName || pIdDoc.name || 'document.pdf',
            pagesMarkups: pIdDoc.pagesMarkups,
            referenceDocumentId: documentId,
          },
        });

        if (result.success) {
          this.$emit('show-viewers-section', 'Document');
        } else {
          warn('RegisterTable', 'openPIdInCurrentViewer: Failed', result.error);
          this.$dialog.notify.warning(result.error || 'Failed to open document', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      /**
       * Open P&ID in another facility (new browser tab)
       */
      openPIdInNewTab(facilityId, tagId, documentId) {
        const route = this.$router.resolve({
          name: 'facility',
          params: { id: facilityId },
          query: {
            tagId: tagId,
            documentId: documentId,
            openDocument: 'true',
          },
        });
        window.open(route.href, '_blank');
      },

      /**
       * Handle P&ID document selection from multi-doc menu
       */
      selectPIdDocument(doc) {
        const tagId = this.pidMenuMatchedTagId;
        if (this.isPIdOtherFacility(doc)) {
          this.openPIdInNewTab(doc.assetId, tagId, doc.id);
        } else {
          this.openPIdInCurrentViewer(doc, tagId);
        }
        this.showPIdMenu = false;
        this.pidMenuTargetRow = null;
      },

      /**
       * Check if a P&ID document is from another facility
       */
      isPIdOtherFacility(doc) {
        return doc.assetId && doc.assetId !== this.$store.state.assetId;
      },

      /**
       * Get facility name from assetId using the facility dictionary
       */
      getFacilityName(assetId) {
        return this.facilityDict[assetId] || 'Other Facility';
      },
    },

    async created() {
      EventBus.$on(REGISTER_EVENTS.LINKED_TAG_SAVED, this.onLinkedTagSaved);
      // Don't load here since the route watcher with immediate: true will handle it
      log('RegisterTable created for register:', this.registerId);
    },

    mounted() {
      this.attachTableObservers();
    },

    async activated() {
      this.attachTableObservers?.();
      // Called when component is activated from keep-alive cache
      // Always check if we need to reload data for a different register
      const currentId = this.currentRegisterId;
      log(
        'RegisterTable activated - Current ID:',
        currentId,
        'Last loaded:',
        this.lastLoadedRegisterId,
      );

      if (!this.lastLoadedRegisterId || this.lastLoadedRegisterId !== currentId) {
        log('Reloading data for register:', currentId);
        // Same ordered sequence as the route watcher, so a `?rowSearch` deep link survives
        // this reset - activated() also fires on first mount, after the watcher has seeded.
        await this.applyRegisterRouteState({
          reset: true,
          rowSearch: this.readRowSearchQuery(this.$route),
        });
      }
    },

    beforeDestroy() {
      EventBus.$off(REGISTER_EVENTS.LINKED_TAG_SAVED, this.onLinkedTagSaved);
      this.detachTableObservers?.();
      if (this.copiedNoticeTimer) {
        clearTimeout(this.copiedNoticeTimer);
        this.copiedNoticeTimer = null;
      }
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      if (this.rowSearchHighlightTimer) {
        clearTimeout(this.rowSearchHighlightTimer);
        this.rowSearchHighlightTimer = null;
      }
      if (this.currentRequestController) {
        this.currentRequestController.abort();
        this.currentRequestController = null;
      }
    },
  };
</script>

<style lang="scss" scoped>
  .register-view-container {
    /* Readable secondary grey (the theme's text_secondary is ~1.6:1 on white) and the amber
       "needs attention" family used by Unassigned rows and the not-configured notices. */
    --register-muted: #59635f;
    --register-warn-bg: #fff8e6;
    --register-warn-border: #f3d58a;
    --register-warn-fg: #7a5200;
    --register-warn-icon: #c98a00;

    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .theme--dark .register-view-container,
  .register-view-container.theme--dark {
    --register-muted: #c8c8c8;
    --register-warn-bg: rgba(210, 160, 0, 0.12);
    --register-warn-border: rgba(210, 160, 0, 0.35);
    --register-warn-fg: #f1d48a;
    --register-warn-icon: #e0b23a;
  }

  .register-data-layout {
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1 1 auto;
    align-items: stretch;
    overflow: hidden;

    > .register-table {
      min-width: 0;
    }

    &--with-details > .register-table {
      margin: var(--ds-space-sm) !important;
    }
  }

  .row-details-panel {
    display: flex;
    width: 392px;
    min-width: 392px;
    max-width: 392px;
    min-height: 0;
    flex: 0 0 392px;
    flex-direction: column;
    margin: 12px 12px 12px 0;
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-md);
    color: var(--v-text-base);
    background: var(--v-bg2-base);
    box-shadow: var(--ds-shadow-2);
    overflow: hidden;

    &:focus-visible {
      outline: var(--ds-focus-ring-width, 2px) solid var(--v-primary-base);
      outline-offset: -2px;
    }

    &__header {
      display: grid;
      min-height: 88px;
      flex: 0 0 auto;
      grid-template-columns: 40px minmax(0, 1fr) 32px;
      align-items: start;
      gap: var(--ds-space-sm);
      padding: var(--ds-space-md);
      border-bottom: 1px solid var(--ds-border-subtle);
      background: var(--v-bg3-base);
    }

    &__mark {
      display: flex;
      width: 40px;
      height: 40px;
      align-items: center;
      justify-content: center;
      border-radius: var(--ds-radius-md);
      background: rgba(3, 98, 76, 0.1);
    }

    &__heading {
      min-width: 0;

      > span {
        display: block;
        margin-bottom: 2px;
        color: var(--v-primary-base);
        font-size: var(--ds-font-micro);
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }

      h2 {
        margin: 0;
        overflow: hidden;
        color: var(--v-text-base);
        font-family: var(--ds-font-family-heading);
        font-size: var(--ds-font-subtitle);
        font-weight: 600;
        line-height: 1.35;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      p {
        margin: 2px 0 0;
        overflow: hidden;
        color: var(--workspace-muted, #59635f);
        font-size: var(--ds-font-caption);
        line-height: 1.4;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &__close {
      margin: -4px -4px 0 0;
    }

    &__body {
      min-height: 0;
      flex: 1 1 auto;
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-gutter: stable;
    }

    &__section {
      padding: var(--ds-space-md);

      + .row-details-panel__section {
        border-top: 1px solid var(--ds-border-subtle);
      }
    }

    &__section-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--ds-space-md);
      margin-bottom: var(--ds-space-sm);

      > div {
        min-width: 0;
      }

      span {
        display: block;
        color: var(--workspace-muted, #59635f);
        font-size: var(--ds-font-micro);
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
      }

      h3 {
        margin: 1px 0 0;
        color: var(--v-text-base);
        font-family: var(--ds-font-family-heading);
        font-size: var(--ds-font-body-sm);
        font-weight: 600;
      }

      > strong {
        display: inline-flex;
        min-width: 28px;
        height: 24px;
        align-items: center;
        justify-content: center;
        padding: 0 7px;
        border-radius: var(--ds-radius-full);
        color: var(--v-primary-base);
        background: rgba(3, 98, 76, 0.1);
        font-size: var(--ds-font-caption);
        font-variant-numeric: tabular-nums;
      }
    }

    &__fields {
      margin: 0;

      &--context {
        margin-top: var(--ds-space-sm);
        padding-top: var(--ds-space-xs);
        border-top: 1px solid var(--ds-border-subtle);
      }

      > div {
        display: grid;
        grid-template-columns: minmax(112px, 0.8fr) minmax(0, 1.2fr);
        gap: var(--ds-space-md);
        padding: 10px 0;
        border-bottom: 1px solid var(--ds-border-subtle);

        &:last-child {
          border-bottom: 0;
        }
      }

      dt {
        color: var(--workspace-muted, #59635f);
        font-size: var(--ds-font-caption);
        font-weight: 500;
        line-height: 1.45;
      }

      dd {
        min-width: 0;
        margin: 0;
        color: var(--v-text-base);
        font-size: var(--ds-font-body-sm);
        font-weight: 500;
        line-height: 1.45;
        overflow-wrap: anywhere;
        white-space: pre-line;
      }
    }

    &__editor {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    &__editor-field {
      // Long text grows with its content up to this many lines, then scrolls inside the box, so one
      // long Findings value never pushes the rest of the panel out of reach.
      $textarea-line-height: 1.75rem; // Vuetify's v-textarea line height
      $textarea-max-lines: 6;

      min-width: 0;

      :deep(.v-input__slot) {
        min-height: 42px !important;
        background: var(--v-bg2-base) !important;
      }

      :deep(.v-textarea textarea) {
        max-height: $textarea-line-height * $textarea-max-lines;
        overflow-y: auto;
      }

      :deep(.field-modified .v-input__slot) {
        border-color: var(--v-primary-base) !important;
        background: rgba(3, 98, 76, 0.04) !important;
      }
    }

    &__bulk-note {
      margin: 0 0 var(--ds-space-sm);
      color: var(--workspace-muted, #59635f);
      font-size: var(--ds-font-caption);
      line-height: 1.45;
    }

    &__changed {
      display: block;
      margin: 4px 2px 0;
      color: var(--workspace-muted, #59635f);
      font-size: var(--ds-font-micro);
      line-height: 1.35;
      overflow-wrap: anywhere;
    }

    &__value--empty {
      color: var(--workspace-muted, #59635f) !important;
      font-weight: 400 !important;
    }

    &__links {
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        border-bottom: 1px solid var(--ds-border-subtle);

        &:last-child {
          border-bottom: 0;
        }

        > button {
          display: grid;
          width: 100%;
          grid-template-columns: 36px minmax(0, 1fr) 22px;
          align-items: center;
          gap: var(--ds-space-sm);
          padding: 10px 4px;
          border: 0;
          border-radius: var(--ds-radius-sm);
          color: inherit;
          background: transparent;
          text-align: left;
          cursor: pointer;
          transition: background-color 120ms ease;

          &:hover:not(:disabled) {
            background: rgba(3, 98, 76, 0.07);
          }

          &:focus-visible {
            outline: 2px solid var(--v-primary-base);
            outline-offset: -2px;
          }

          &:disabled {
            cursor: default;
          }
        }

        strong {
          overflow: hidden;
          color: var(--v-text-base);
          font-size: var(--ds-font-body-sm);
          font-weight: 600;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        span {
          margin-top: 1px;
          color: var(--workspace-muted, #59635f);
          font-size: var(--ds-font-caption);
        }
      }
    }

    &__link-copy {
      display: flex;
      min-width: 0;
      flex-direction: column;
    }

    &__link-mark {
      display: flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border-radius: var(--ds-radius-md);
      background: rgba(3, 98, 76, 0.08);
    }

    &__empty {
      display: flex;
      min-height: 144px;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: var(--ds-space-xs);
      padding: var(--ds-space-md);
      color: var(--workspace-muted, #59635f);
      text-align: center;

      &--compact {
        min-height: 120px;
      }

      strong {
        color: var(--v-text-base);
        font-size: var(--ds-font-body-sm);
        font-weight: 600;
      }

      span {
        max-width: 260px;
        font-size: var(--ds-font-caption);
        line-height: 1.45;
      }
    }

    &__footer {
      display: flex;
      flex: 0 0 auto;
      align-items: flex-start;
      gap: var(--ds-space-sm);
      padding: 12px var(--ds-space-md);
      border-top: 1px solid var(--ds-border-subtle);
      color: var(--workspace-muted, #59635f);
      background: var(--v-bg3-base);
      font-size: var(--ds-font-caption);
      line-height: 1.4;

      &--actions {
        align-items: center;
        justify-content: flex-end;
        gap: var(--ds-space-sm);
        padding: 10px var(--ds-space-md);
      }
    }
  }

  .row-details-panel-enter-active,
  .row-details-panel-leave-active {
    transition: opacity var(--ds-transition-default), transform var(--ds-transition-default);
  }

  .row-details-panel-enter,
  .row-details-panel-leave-to {
    opacity: 0;
    transform: translateX(12px);
  }

  .v-simple-checkbox {
    width: 100%;
    display: flex;
    text-align: center;
    justify-content: center;
    align-items: center;
    height: -webkit-fill-available;
  }

  /* ==========================================
     ENHANCED TABLE TOOLBAR
     Matches RegisterManagement style
     ========================================== */

  .table-toolbar-enhanced {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: var(--v-bg2-base);
    border-bottom: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
    gap: 16px;
    flex-wrap: wrap;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .toolbar-title-section {
    display: flex;
    align-items: center;
  }

  .toolbar-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--v-text-base);
  }

  .count-chip {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .toolbar-divider {
    height: 24px;
    opacity: 0.3;
  }

  .selection-chip {
    font-weight: 500;
  }

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .search-field {
    width: 240px;
    max-width: 100%;

    ::v-deep .v-input__slot {
      min-height: 36px !important;
    }

    ::v-deep input {
      font-size: 13px;
    }
  }

  .export-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
  }

  /* Notice strips under the toolbar stay neutral; severity is the icon's job. */
  .register-data-notice {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
    color: var(--register-muted);
    font-size: 13px;
    line-height: 1.45;

    > span {
      min-width: 0;
      flex: 1 1 auto;
    }

    strong {
      color: var(--v-text-base);
      font-weight: 600;
    }

    .register-data-notice__icon--warning { color: var(--register-warn-icon); }
  }

  /* Toolbar: ownership summary (central Separated), scope hint, Facility filter */
  .register-ownership-chip {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  .register-unassigned-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 10px;
    border: 1px solid var(--register-warn-border);
    border-radius: var(--ds-radius-full, 9999px);
    background: var(--register-warn-bg);
    color: var(--register-warn-fg);
    font-family: inherit;
    font-size: var(--ds-font-caption, 12px);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    cursor: pointer;

    .v-icon { color: inherit; }

    &:hover { filter: brightness(0.97); }

    &:focus-visible {
      outline: var(--ds-focus-ring-width, 2px) solid var(--v-primary-base);
      outline-offset: 2px;
    }

    &[aria-pressed='true'] {
      border-color: var(--register-warn-icon);
      background: var(--register-warn-icon);
      color: #1f1400;
    }
  }

  .register-scope-hint {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--register-muted);
    font-size: var(--ds-font-caption, 12px);
    white-space: nowrap;

    .v-icon { color: var(--v-primary-base); }
  }

  .register-facility-filter {
    max-width: 260px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;

    &__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  /* The menu is teleported to the app root, outside .register-view-container: redeclare its tokens. */
  .register-facility-filter-menu {
    --register-muted: #59635f;
    --register-warn-fg: #7a5200;

    min-width: 240px;

    &.theme--dark {
      --register-muted: #c8c8c8;
      --register-warn-fg: #f1d48a;
    }

    &__check {
      min-width: 20px;
      margin-right: var(--ds-space-sm, 8px) !important;
    }

    &__warning { color: var(--register-warn-fg); }

    &__count {
      margin-left: var(--ds-space-md, 16px);
      color: var(--register-muted);
      font-size: var(--ds-font-caption, 12px);
      font-variant-numeric: tabular-nums;
    }
  }

  .register-copied {
    color: var(--v-primary-base);
    font-size: var(--ds-font-caption, 12px);
  }

  .theme--dark {
    .register-scope-hint .v-icon,
    .register-copied { color: var(--v-secondary-base); }
  }

  .relationship-attention-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
    color: var(--v-text-base);

    .v-icon { color: var(--v-warning-base); }

    &--error .v-icon { color: var(--v-error-base); }

    &__content {
      display: flex;
      min-width: 0;
      flex: 1 1 auto;
      flex-direction: column;
      gap: 2px;
      font-size: 13px;
      line-height: 1.4;

      strong {
        font-size: 14px;
        font-weight: 600;
      }

      code {
        color: inherit;
        font-size: 12px;
        overflow-wrap: anywhere;
      }
    }

    &__meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 4px;
      color: var(--v-text2-base);
      font-size: 11px;
      overflow-wrap: anywhere;
    }

    &__count {
      flex: 0 0 auto;
      font-variant-numeric: tabular-nums;
    }
  }

  /* Responsive adjustments */
  @media (max-width: 960px) {
    .table-toolbar-enhanced {
      flex-direction: column;
      align-items: stretch;
    }

    .toolbar-left,
    .toolbar-right {
      justify-content: flex-start;
    }

    .search-field {
      width: 100%;
      max-width: 300px;
    }

    .toolbar-divider {
      display: none;
    }

    .relationship-attention-banner {
      align-items: flex-start;
      flex-wrap: wrap;

      &__content {
        flex-basis: calc(100% - 44px);
      }
    }
  }

  /* Dark theme adjustments */
  .theme--dark .table-toolbar-enhanced {
    background: var(--v-bg3-base);
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .theme--dark .row-details-panel {
    --workspace-muted: #c8c8c8;

    &__mark,
    &__link-mark,
    &__section-heading > strong {
      background: rgba(44, 194, 149, 0.14);
    }
  }

  .theme--dark .relationship-attention-banner {
    background: rgba(255, 152, 0, 0.12);

    &--error {
      background: rgba(244, 67, 54, 0.12);
    }
  }

  /* ==========================================
     TABLE (AVA-602 showcase B, DATA_TABLE_STANDARD)
     Neutral slate header/footer (same tokens as Register Management), hairline rows, zebra only
     at 12+ columns, left block (select, #, Linked Tags, Facility) and Actions pinned. Row tints are
     painted on the cells: pinned cells are opaque, so a tint on the <tr> would never show on them.
     ========================================== */

  .register-table {
    --register-cell-bg: var(--ds-surface-elevated, #ffffff);
    --register-row-hover: rgba(3, 98, 76, 0.05);
    --register-row-open: rgba(3, 98, 76, 0.08);
    --register-row-editing: rgba(33, 150, 243, 0.04);
    --register-pin-divider: var(--ds-border-default, rgba(0, 0, 0, 0.12));
    --register-pin-shadow: rgba(0, 0, 0, 0.16); /* 1px offset, 1px blur, no spread: reads as an edge, not a cast shadow */

    flex: 1;
    margin: 12px;
    border: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
    border-radius: var(--ds-radius-md, 8px);
    background-color: var(--register-cell-bg) !important;
    overflow: hidden !important;

    ::v-deep .v-data-table__wrapper {
      overflow: auto;
      border: 0;
      border-radius: 0 !important;
      /* App.vue tints every sticky table wrapper at 4% grey; here the body must stay the table's
         white surface to the footer, also while loading or empty (Khalid review). */
      background-color: var(--register-cell-bg) !important;
    }
  }

  .theme--dark .register-table {
    --register-row-hover: rgba(44, 194, 149, 0.06);
    --register-row-open: rgba(44, 194, 149, 0.1);
    --register-row-editing: rgba(33, 150, 243, 0.08);
    --register-pin-shadow: rgba(0, 0, 0, 0.5);
  }

  /* Header: sticky, compact, slate tint with a hairline under it. Above pinned body cells. */
  .register-table :deep(thead.v-data-table-header) {
    position: sticky !important;
    top: 0 !important;
    z-index: var(--z-table-sticky-header) !important;
    height: auto;
    border: 0;
    box-shadow: none !important;
    background: var(--register-cell-bg) !important;
  }

  .register-table :deep(thead.v-data-table-header > tr:not(.v-data-table__progress) > th) {
    height: 44px !important;
    padding: 0 8px !important;
    vertical-align: middle !important;
    font-size: var(--ds-font-caption, 12px) !important;
    font-weight: 600;
    white-space: nowrap;
    color: var(--table-header-text) !important;
    border: 0 !important;
    background-color: var(--register-cell-bg) !important;
    background-image: linear-gradient(var(--table-header-tint), var(--table-header-tint)) !important;
    box-shadow: inset 0 -1px 0 var(--table-border-hairline) !important;
  }

  /* Footer (pagination): compact and tinted like the header so the table reads as one block */
  .v-application--is-ltr .register-table.v-data-table--fixed-header :deep(.v-data-footer) {
    margin-right: 0;
  }

  .register-table :deep(.v-data-footer) {
    border: 0 !important;
    border-top: thin solid var(--table-border-hairline) !important;
    border-radius: 0 !important;
    background-color: var(--register-cell-bg) !important;
    background-image: linear-gradient(var(--table-header-tint), var(--table-header-tint));
    font-size: var(--ds-font-caption, 12px);
  }

  /* Body cells: hairline rows on an opaque surface */
  .register-table:not(.v-data-table--mobile) :deep(tbody > tr > td) {
    height: var(--table-row-default, 48px) !important;
    border: 0 !important;
    border-bottom: 1px solid var(--table-border-hairline) !important;
    background-color: var(--register-cell-bg);
  }

  .register-table :deep(tbody > tr:last-child > td) {
    border-bottom: 0 !important;
  }

  .register-table.register-table--zebra :deep(tbody > tr:nth-child(even) > td) {
    background-image: linear-gradient(var(--table-row-hover), var(--table-row-hover));
  }

  .register-table :deep(tbody > tr:not(.v-data-table__empty-wrapper):hover > td),
  .register-table :deep(tbody > tr:not(.v-data-table__empty-wrapper):focus-within > td) {
    background-image: linear-gradient(var(--register-row-hover), var(--register-row-hover));
  }

  .register-table :deep(tbody > tr.v-data-table__empty-wrapper:hover) {
    background: transparent !important;
  }

  .register-table :deep(.register-cell--number) {
    text-align: right !important;
    font-variant-numeric: tabular-nums;
  }

  /* Pinned column widths (PINNED_COLUMN_WIDTH in the script uses the same numbers) */
  .register-table:not(.v-data-table--mobile) :deep(.register-col--select) {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-col--index) {
    width: 88px;
    min-width: 88px;
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-col--links) {
    width: 288px;
    min-width: 288px;
    max-width: 288px;
  }

  .register-table:not(.v-data-table--mobile) :deep(td.register-col--links) {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    white-space: normal;
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-col--facility) {
    width: 184px;
    min-width: 184px;
    max-width: 184px;
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-col--actions) {
    width: 112px;
    min-width: 112px;
  }

  /* Pinned blocks (desktop table only; the stacked mobile rows have nothing to pin) */
  .register-table:not(.v-data-table--mobile) :deep(.register-pin) {
    position: sticky;
    z-index: var(--z-table-sticky-col);
  }

  /* Inside the sticky header: pinned header cells stay above the scrolling header cells */
  .register-table:not(.v-data-table--mobile) :deep(thead .register-pin) {
    z-index: 3;
  }

  .register-table :deep(.register-pin--l0) { left: var(--register-pin-l0, 0px); }
  .register-table :deep(.register-pin--l1) { left: var(--register-pin-l1, 0px); }
  .register-table :deep(.register-pin--l2) { left: var(--register-pin-l2, 0px); }
  .register-table :deep(.register-pin--l3) { left: var(--register-pin-l3, 0px); }
  .register-table :deep(.register-pin--right) { right: 0; }

  .register-table:not(.v-data-table--mobile) :deep(td.register-pin--edge) {
    box-shadow: inset -1px 0 0 var(--register-pin-divider);
    transition: box-shadow var(--ds-transition-fast, 0.15s ease);
  }

  .register-table:not(.v-data-table--mobile) :deep(td.register-pin--right) {
    box-shadow: inset 1px 0 0 var(--register-pin-divider);
    transition: box-shadow var(--ds-transition-fast, 0.15s ease);
  }

  .register-table:not(.v-data-table--mobile) :deep(thead th.register-pin--edge) {
    box-shadow: inset 0 -1px 0 var(--table-border-hairline), inset -1px 0 0 var(--register-pin-divider) !important;
  }

  .register-table:not(.v-data-table--mobile) :deep(thead th.register-pin--right) {
    box-shadow: inset 0 -1px 0 var(--table-border-hairline), inset 1px 0 0 var(--register-pin-divider) !important;
  }

  /* Soft edge shadows only while content scrolls under a pinned block */
  .register-table:not(.v-data-table--mobile) :deep(.register-table__wrapper--scrolled td.register-pin--edge) {
    box-shadow: 1px 0 1px 0 var(--register-pin-shadow), inset -1px 0 0 var(--register-pin-divider);
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-table__wrapper--scrolled thead th.register-pin--edge) {
    box-shadow: inset 0 -1px 0 var(--table-border-hairline), 1px 0 1px 0 var(--register-pin-shadow),
      inset -1px 0 0 var(--register-pin-divider) !important;
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-table__wrapper--more-right td.register-pin--right) {
    box-shadow: -1px 0 1px 0 var(--register-pin-shadow), inset 1px 0 0 var(--register-pin-divider);
  }

  .register-table:not(.v-data-table--mobile) :deep(.register-table__wrapper--more-right thead th.register-pin--right) {
    box-shadow: inset 0 -1px 0 var(--table-border-hairline), -1px 0 1px 0 var(--register-pin-shadow),
      inset 1px 0 0 var(--register-pin-divider) !important;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--ds-space-lg, 24px) 0;
  }

  /* Facility column (central Separated): owner name, or an em-dash + amber "Unassigned" */
  .register-facility-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;

    > span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .v-icon { color: var(--register-muted); }

    &--unassigned {
      color: var(--register-muted);

      i {
        color: var(--register-warn-fg);
        font-style: normal;
        font-weight: 500;
      }
    }

    &__dot {
      width: 6px;
      height: 6px;
      flex: 0 0 6px;
      border-radius: 50%;
      background: var(--register-warn-icon);
    }
  }

  .register-empty-value {
    color: var(--register-muted);
    font-size: var(--ds-font-caption, 12px);
  }

  /* Row actions: two primary icons on row hover / keyboard focus, ⋮ always visible */
  .register-row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
  }

  .register-row-actions__primary {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    opacity: 0;
    transition: opacity var(--ds-transition-fast, 0.15s ease);
  }

  .register-table :deep(tbody > tr:hover .register-row-actions__primary),
  .register-table :deep(tbody > tr:focus-within .register-row-actions__primary),
  .register-table :deep(tbody > tr.details-row .register-row-actions__primary) {
    opacity: 1;
  }

  /* Touch: no hover, so the primary icons stay visible (nothing becomes unreachable) */
  @media (hover: none) {
    .register-row-actions__primary { opacity: 1; }
  }

  .register-row-menu {
    min-width: 224px;
  }

  .register-row-menu__icon {
    margin-right: var(--ds-space-sm, 8px) !important;
  }

  .date-cell {
    display: inline-flex;
    align-items: center;
    color: #6b778c;
    font-size: 0.875rem;
    border: 1px solid rgba(176, 190, 197, 0.2);
    padding: 4px 8px;
    border-radius: 4px;
    width: fit-content;
  }

  .theme--dark .date-cell {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .display-field {
    padding: 8px;
    border-radius: 4px;
    min-height: 20px;
    display: inline-block;
    text-overflow: ellipsis;
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
    width: 95%;
  }

  .edit-field-container {
    position: relative;
    min-height: 60px;
    padding: 4px 0;
  }

  .field-modified {
    :deep(.v-input__control) {
      .v-input__slot {
        border-color: #ff9800 !important;
        background-color: rgba(255, 152, 0, 0.05) !important;
        transition: all 0.3s ease;
      }
    }

    :deep(.v-text-field__details) {
      display: none !important;
    }
  }

  .field-unchanged {
    :deep(.v-input__control) {
      .v-input__slot {
        background-color: rgba(0, 0, 0, 0.02);
        transition: all 0.3s ease;
      }
    }

    :deep(.v-text-field__details) {
      display: none !important;
    }
  }

  .field-change-indicator {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 2px 8px;
    margin-top: -20px;

    .original-value {
      font-weight: 500;
      color: rgba(0, 0, 0, 0.6);
      font-style: italic;
    }
  }

  /* Row states, painted on the cells (see TABLE above) */
  :deep(.v-data-table tbody tr) {
    &.editing-row > td {
      background-image: linear-gradient(var(--register-row-editing), var(--register-row-editing)) !important;
      padding-top: 12px !important;
      padding-bottom: 12px !important;
      vertical-align: top !important;
    }

    &.editing-row > td:first-child {
      box-shadow: inset 4px 0 0 #2196f3;
    }

    &.details-row:not(.editing-row) > td {
      background-image: linear-gradient(var(--register-row-open), var(--register-row-open)) !important;
    }

    &.details-row:not(.editing-row) > td:first-child {
      box-shadow: inset 3px 0 0 var(--v-primary-base);
    }

    /* One-time flash for a row reached through a `?rowSearch` deep link. Painted on the
       cells, not the row, so it stays visible over the row-level hover/selection tints. */
    &.row-search-flash > td {
      animation: row-search-flash 2s ease-out 1;
    }
  }

  @keyframes row-search-flash {
    0% {
      background-color: rgba(44, 194, 149, 0.38);
    }

    60% {
      background-color: rgba(44, 194, 149, 0.2);
    }

    100% {
      background-color: transparent;
    }
  }

  /* Reduced motion still needs the row to stand out - it just holds a static tint until
     the flash window closes and the class is removed. */
  @media (prefers-reduced-motion: reduce) {
    :deep(.v-data-table tbody tr.row-search-flash > td) {
      animation: none;
      background-color: rgba(44, 194, 149, 0.16);
    }
  }

  /* Animation for field changes */
  .field-modified {
    animation: field-highlight 0.5s ease-in-out;
  }

  @keyframes field-highlight {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.02);
    }
    100% {
      transform: scale(1);
    }
  }

  // Custom scrollbar
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c7d0;
    border-radius: 4px;
    &:hover {
      background: #97a0af;
    }
  }

  // Column filter styles
  .header-with-filter {
    min-height: 0;

    .header-text {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 600;
    }

    .filter-btn {
      opacity: 0.6;
      transition: all 0.2s ease;
      margin-left: 4px;

      &:hover {
        opacity: 1;
        background-color: rgba(33, 150, 243, 0.04);
      }

      &.filter-active {
        opacity: 1;
        background-color: rgba(33, 150, 243, 0.08);

        .v-icon {
          color: #1976d2 !important;
        }
      }
    }
  }

  .filter-menu {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15) !important;
    border-radius: 8px;
    overflow: hidden;

    .v-card__title {
      background-color: var(--v-bg1-base);
      border-bottom: 1px solid var(--v-bg1-base);

      .text-subtitle-2 {
        font-weight: 600;
        color: var(--v-text-primary);
      }
    }

    .v-card__text {
      padding: 16px !important;

      .v-radio-group {
        .v-input--radio-group__input {
          margin-top: 0;
        }

        .v-radio {
          margin-right: 12px;

          :deep(.v-label) {
            font-size: 0.875rem;
            font-weight: 500;
          }
        }
      }
    }

    .v-card__actions {
      border-top: 1px solid var(--v-bg1-base);
      background-color: var(--v-bg1-base);
    }
  }

  // Header styling enhancements
  :deep(.v-data-table-header th) {
    padding-left: 8px !important;
    padding-right: 8px !important;

    /* One line: title, then its filter button right after it (not pushed to the far edge, where it
       read as belonging to the next column); Vuetify's sort arrow stays on the same line. */
    white-space: nowrap;

    .header-with-filter {
      display: inline-flex !important;
      max-width: calc(100% - 20px);
      vertical-align: middle;
      justify-content: flex-start;
    }

    .v-data-table-header__icon {
      vertical-align: middle;
    }

    /* Filter button: quiet until the header is hovered/focused, always shown when a filter is on */
    .filter-btn:not(.filter-active) {
      opacity: 0;
    }

    &:hover .filter-btn,
    &:focus-within .filter-btn {
      opacity: 0.7;
    }

    @media (hover: none) {
      .filter-btn:not(.filter-active) {
        opacity: 0.6;
      }
    }
  }

  // Filter button hover states
  :deep(.v-btn--icon.filter-btn) {
    width: 28px !important;
    height: 28px !important;

    .v-icon {
      font-size: 16px !important;
    }
  }

  // Table toolbar styles
  .table-toolbar {
    background-color: var(--v-bg1-base);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    min-height: 48px;
  }

  .export-actions {
    .v-btn {
      text-transform: none;
      font-weight: 500;
      letter-spacing: 0;
    }
  }

  // Selection column styling
  :deep(th:first-child),
  :deep(td:first-child) {
    padding-left: 8px !important;
    padding-right: 0 !important;
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
  }

  // Selected row highlight
  :deep(.v-data-table tbody tr.selected-row > td) {
    background-image: linear-gradient(rgba(33, 150, 243, 0.08), rgba(33, 150, 243, 0.08)) !important;
  }

  // Sort index indicator & toggle styles
  .sort-index-indicator {
    font-size: 14px !important;
    opacity: 0.85;
  }

  .sort-index-toggle {
    background-color: rgba(0, 0, 0, 0.02);

    .sort-index-switch {
      ::v-deep .v-input__control {
        min-height: auto;

        .v-input__slot {
          margin-bottom: 0;
        }
      }
    }
  }

  .theme--dark .sort-index-toggle {
    background-color: rgba(255, 255, 255, 0.03);
  }

  // P&ID menu styles
  .pid-menu {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15) !important;
    border-radius: 8px;
    overflow: hidden;
    max-width: 400px;

    .v-subheader {
      font-weight: 600;
      font-size: 0.8125rem;
    }

    .v-list-item {
      min-height: 44px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .row-details-panel-enter-active,
    .row-details-panel-leave-active {
      transition-duration: 0.01ms !important;
    }
  }
</style>
